import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react'
import {CentersAndApps} from '../components/catalog-components/centers-and-apps'
import {CommandCenterLayout} from '../components/command-center-layout'
import {Options} from '../components/catalog-components/options'
import {OrderSummary} from '../components/catalog-components/order-summary'
import {useAuthClient} from '../utils/use-auth-client'
import {resolveApplicableFees} from '../utils/feeUtils'
import {getAmountToQuantityMap} from '../utils/lift-lead-options'
import {toast} from 'react-toastify'
import dayjs from 'dayjs'
import {useSelector, useDispatch} from 'react-redux'
import {shallowEqual} from 'react-redux'
import {
  selectManualCoupons,
  selectUpgradePaymentPreview,
  setUpgradePaymentPreview as setUpgradePaymentPreviewAction,
  resetAllCouponState,
} from '../app-store/coupon'

import {
  // commandCenterIcon,
  // marketingCenterIcon,
  // reportingCenterIcon,
  // workforceCenterIcon,
  commandCenterIconV1,
  enhancedAccessibilityIcon,
  signaturesIcon,
  thryvLeadsIcon,
  // businessCenterIcon,
  seoIcon,
  hipaaIcon,
  websiteBuilderIcon,
  cloverAppIcon,
  constantContactIcon,
  copperAppIcon,
  xeroAppIcon,
  googleSheetAppIcon,
  hubspotAppIcon,
  insightlyAppIcon,
  instagramAppIcon,
  jotFormIcon,
  mailchimpIcon,
  myobAppIcon,
  quickbooksAppIcon,
  quickbooksDesktopIcon,
  ringCentralIcon,
  shopifyAppIcon,
  smartDataImporterIcon,
  twillioAppIcon,
  webchatAppIcon,
  facebookMessengerIconApp,
  typeFormIcon,
  restrictedAccessIcon,
  gmailLogo,
  // marketplaceIcon,
  logoBuilderIcon,
  customVideoShootIcon,
  listingsAppIcon,
  growthPackageIcon,
  seoKeywordsIcon,
  seoPowerboostIcon,
  keapMaxIcon,
  marketingProEditorIcon,
  professionalServiceIcon,
  socialAdsIcon,
  // thryvPayLogo,
  thryvPayLogov1,
  zapierPromotionIcon,
  ThryvSocialPostsIcon,
  complianceIcon,
  connectIcon,
  digitalThryvToolsIcon,
  diyWebsiteIcon,
  // kaddIcon,
  reverseGreenPlusIcon,
  manageContactsIcon,
  messagingv2Icon,
  oneHourIcon,
  phoneMessageIcon,
  profileIcon,
  supportV2Icon,
  // tAddIcon,
  reverseOrangePlusIcon,
  textingIcon,
  uploadDocumentIcon,
  videoSupportIcon,
  worldIcon,
  reportingCenterIconv1,
  workforceCenterIconv1,
  marketingCenterIconv1,
  thryvGrowthPlatformIcon,
  businessCenterIconv1,
  supportCenter,
  keapQuickLaunchIcon,
  listingRefreshIcon,
  strategicContentCreationIcon,
  plusThisIcon,
  callTrackingNumberIcon,
  geoAddOnIcon,
} from '../assets'
import {COUNTRIES, CC_CURRENCIES, FREE_ACCOUNT_CODES} from '../utils/constants'
import {useLocation} from 'react-router'
import {structuredClone} from 'core-js'
import {UpdateAddressModal} from '../components/update-address-modal'
import {
  BC_PLANS,
  MC_PLANS,
  SOCIAL_ADS_BNI_PLAN_CODES,
  KEAP_FAMILY_CODES,
  MC_2_0_SIGPREMIERPLANS,
  MC_2_0_PLANS,
  KEAP_ONLY_COUNTRIES,
  MC_2_0_SIGNATUREPLANS,
  MC_TRIAL_PLAN_CODES,
  GEO_ADDON_CODES,
} from '../utils/constants'
import {
  THRYV_BASIC_CODES,
  isTgpFoundationFamilyCode,
} from '../utils/tgp-catalog-flags'
import {useUserFeatures} from '../context/user-features-context'
import {RETAIN_WEBSITE_MODAL_STATES} from '../components/catalog-components/retain-website-modal'

const COMMAND_CENTER_NAME_MAPPING = {
  'cc_basic-5-thryv-m2m': 'My Account',
  'cc_basicpls-5-thryv-m2m': 'My Account',
  'cc_plus-5-thryv-m2m': 'My Account Plus',
  'cc_pro-5-thryv-m2m': 'My Account Pro',
}

const KEAP_TEXT_VOICE_STANDALONE_CODES = new Set([
  'addon-5-keap-textvoice-m2m',
  'addon-5-keap-textvoice-annual',
])

const KEAP_INCLUDED_TEXT_VOICE_ENTITLEMENT_CODES = new Set([
  'ent-keap-included-txtvoice',
  'ent-keap-included-txtvoice-annual',
])

const normalizeProductName = name => (name || '').toLowerCase().trim()

const getProductIdentity = product => {
  const code = String(product?.code || '')
    .toLowerCase()
    .trim()
  const name = normalizeProductName(product?.name)
  return code || name
}

const mergeUniqueProducts = (items = [], additions = []) => {
  const existingKeys = new Set(items.map(getProductIdentity).filter(Boolean))
  const next = [...items]

  additions.forEach(product => {
    const key = getProductIdentity(product)
    if (!key || existingKeys.has(key)) return
    existingKeys.add(key)
    next.push(product)
  })

  return next
}

const firstPresentValue = (...values) =>
  values.find(
    value => value !== undefined && value !== null && String(value).trim(),
  )

const getSubscriptionBillTo = response => {
  const childSubscriptionAccount = response?.childSubscriptions?.find(
    sub => sub?.account,
  )?.account

  const billTo = firstPresentValue(
    response?.accountInfo?.billTo,
    response?.recurlyAccount?.billTo,
    response?.recurlyAccount?.bill_to,
    childSubscriptionAccount?.billTo,
    childSubscriptionAccount?.bill_to,
  )

  if (billTo) return billTo

  if (
    response?.recurlyAccount?.parentAccountId ||
    childSubscriptionAccount?.parentAccountId ||
    response?.parentSubscriptions?.length
  ) {
    return 'parent'
  }

  return ''
}

const isKeapCenterCode = code => {
  const normalizedCode = String(code || '').toLowerCase()
  return KEAP_FAMILY_CODES.includes(code) || normalizedCode.startsWith('keap_')
}

const isStandaloneKeapTextVoiceProduct = product => {
  const normalizedCode = String(product?.code || '')
    .toLowerCase()
    .trim()
  const normalizedName = normalizeProductName(product?.name)

  return (
    KEAP_TEXT_VOICE_STANDALONE_CODES.has(normalizedCode) ||
    (normalizedName.includes('keap') &&
      normalizedName.includes('text') &&
      normalizedName.includes('voice') &&
      !normalizedName.includes('included'))
  )
}

const isIncludedKeapTextVoiceProduct = product => {
  const normalizedCode = String(product?.code || '')
    .toLowerCase()
    .trim()
  const normalizedName = normalizeProductName(product?.name)

  return (
    KEAP_INCLUDED_TEXT_VOICE_ENTITLEMENT_CODES.has(normalizedCode) ||
    (normalizedName.includes('included') &&
      normalizedName.includes('keap') &&
      normalizedName.includes('text') &&
      normalizedName.includes('voice'))
  )
}

const syncCoreKeapTextVoiceOverride = (
  cartData,
  defaultCartData = {centers: [], apps: []},
) => {
  if (!cartData) return cartData

  const cartCenters = Array.isArray(cartData.centers) ? cartData.centers : []
  const cartApps = Array.isArray(cartData.apps) ? cartData.apps : []
  const defaultCenters = Array.isArray(defaultCartData?.centers)
    ? defaultCartData.centers
    : []
  const defaultApps = Array.isArray(defaultCartData?.apps)
    ? defaultCartData.apps
    : []

  const hasStandaloneKeapTextVoice = [...cartCenters, ...cartApps].some(
    isStandaloneKeapTextVoiceProduct,
  )

  const stripIncludedFromCenter = center => {
    const currentEntitlements = Array.isArray(center?.entitlements)
      ? center.entitlements
      : []
    const currentAddons = Array.isArray(center?.addons) ? center.addons : []

    const nextEntitlements = currentEntitlements.filter(
      entitlement => !isIncludedKeapTextVoiceProduct(entitlement),
    )
    const nextAddons = currentAddons.filter(
      addon => !isIncludedKeapTextVoiceProduct(addon),
    )

    if (
      nextEntitlements.length === currentEntitlements.length &&
      nextAddons.length === currentAddons.length
    ) {
      return center
    }

    return {
      ...center,
      entitlements: nextEntitlements,
      addons: nextAddons,
    }
  }

  if (hasStandaloneKeapTextVoice) {
    const nextCenters = cartCenters
      .filter(center => !isIncludedKeapTextVoiceProduct(center))
      .map(stripIncludedFromCenter)
    const nextApps = cartApps.filter(
      app => !isIncludedKeapTextVoiceProduct(app),
    )

    const centersChanged =
      nextCenters.length !== cartCenters.length ||
      nextCenters.some((center, index) => center !== cartCenters[index])
    const appsChanged =
      nextApps.length !== cartApps.length ||
      nextApps.some((app, index) => app !== cartApps[index])

    if (!centersChanged && !appsChanged) {
      return cartData
    }

    return {
      ...cartData,
      centers: nextCenters,
      apps: nextApps,
    }
  }

  let nextCenters = cartCenters.filter(Boolean).map(center => ({...center}))
  let nextApps = cartApps.filter(Boolean).map(app => ({...app}))
  let didChange = false

  const defaultIncludedCenters = defaultCenters.filter(
    isIncludedKeapTextVoiceProduct,
  )
  const defaultIncludedApps = defaultApps.filter(isIncludedKeapTextVoiceProduct)

  const mergedCenters = mergeUniqueProducts(nextCenters, defaultIncludedCenters)
  if (mergedCenters.length !== nextCenters.length) {
    nextCenters = mergedCenters
    didChange = true
  }

  const mergedApps = mergeUniqueProducts(nextApps, defaultIncludedApps)
  if (mergedApps.length !== nextApps.length) {
    nextApps = mergedApps
    didChange = true
  }

  const keapCenterIndex = nextCenters.findIndex(({code}) =>
    isKeapCenterCode(code),
  )
  if (keapCenterIndex >= 0) {
    const currentKeapCenter = nextCenters[keapCenterIndex]
    const defaultKeapCenter =
      defaultCenters.find(({code}) => code === currentKeapCenter?.code) ||
      defaultCenters.find(({code}) => isKeapCenterCode(code))

    const currentEntitlements = Array.isArray(currentKeapCenter?.entitlements)
      ? currentKeapCenter.entitlements
      : []
    const currentAddons = Array.isArray(currentKeapCenter?.addons)
      ? currentKeapCenter.addons
      : []

    const defaultIncludedEntitlements = (
      Array.isArray(defaultKeapCenter?.entitlements)
        ? defaultKeapCenter.entitlements
        : []
    ).filter(isIncludedKeapTextVoiceProduct)

    const defaultIncludedAddons = (
      Array.isArray(defaultKeapCenter?.addons) ? defaultKeapCenter.addons : []
    ).filter(isIncludedKeapTextVoiceProduct)

    const nextEntitlements = mergeUniqueProducts(
      currentEntitlements,
      defaultIncludedEntitlements,
    )
    const nextAddons = mergeUniqueProducts(currentAddons, defaultIncludedAddons)

    if (
      nextEntitlements.length !== currentEntitlements.length ||
      nextAddons.length !== currentAddons.length
    ) {
      nextCenters[keapCenterIndex] = {
        ...currentKeapCenter,
        entitlements: nextEntitlements,
        addons: nextAddons,
      }
      didChange = true
    }
  }

  if (!didChange) {
    return cartData
  }

  return {
    ...cartData,
    centers: nextCenters,
    apps: nextApps,
  }
}

const getCustomFieldValue = (customFields, fieldNames = []) => {
  if (!customFields || !fieldNames?.length) return null

  if (Array.isArray(customFields)) {
    const lowerCaseFieldNames = fieldNames.map(field => field.toLowerCase())
    const field = customFields.find(item =>
      lowerCaseFieldNames.includes(String(item?.name || '').toLowerCase()),
    )
    return field?.value ?? null
  }

  if (typeof customFields === 'object') {
    for (const name of fieldNames) {
      if (customFields[name] !== undefined && customFields[name] !== null) {
        return customFields[name]
      }
    }
  }

  return null
}

const PLANS_ICONS = [
  {
    name: 'my account',
    icon: commandCenterIconV1,
  },
  {
    name: 'business center',
    icon: businessCenterIconv1,
  },
  {
    name: 'marketing center',
    icon: marketingCenterIconv1,
  },
  {
    name: 'reporting center',
    icon: reportingCenterIconv1,
  },
  {
    name: 'workforce center',
    icon: workforceCenterIconv1,
  },
  {
    name: 'keap max',
    icon: keapMaxIcon,
  },
  {
    name: 'keap pro',
    icon: keapMaxIcon,
  },
  {
    name: 'keap ultimate',
    icon: keapMaxIcon,
  },
  {
    name: 'keap_pro',
    icon: keapMaxIcon,
  },
  {
    name: 'keap_ultimate',
    icon: keapMaxIcon,
  },
  {
    name: 'keap_max',
    icon: keapMaxIcon,
  },
  {
    name: 'hvac',
    icon: keapMaxIcon,
  },
  {
    name: 'roofing',
    icon: keapMaxIcon,
  },
  {
    name: 'electrical',
    icon: keapMaxIcon,
  },
  {
    name: 'plumbing',
    icon: keapMaxIcon,
  },
  {
    name: 'insurance',
    icon: keapMaxIcon,
  },
  {
    name: 'vertical services',
    icon: keapMaxIcon,
  },
  {
    name: 'vertical and mc services',
    icon: keapMaxIcon,
  },
  {
    name: 'vertical and mc services & design',
    icon: keapMaxIcon,
  },
  {
    name: 'vertical success services',
    icon: keapMaxIcon,
  },
  {
    name: 'enhanced accessibility',
    icon: enhancedAccessibilityIcon,
  },
  {
    name: 'signatures',
    icon: signaturesIcon,
  },
  {
    name: 'leads',
    icon: thryvLeadsIcon,
  },
  {
    name: 'seo',
    icon: seoIcon,
  },
  {
    name: 'hipaa',
    icon: hipaaIcon,
  },
  {
    name: 'website',
    icon: websiteBuilderIcon,
  },
  {
    name: 'clover',
    icon: cloverAppIcon,
  },
  {
    name: 'constant contact',
    icon: constantContactIcon,
  },
  {
    name: 'copper',
    icon: copperAppIcon,
  },
  {
    name: 'xero app',
    icon: xeroAppIcon,
  },
  {
    name: 'google sheets',
    icon: googleSheetAppIcon,
  },
  {
    name: 'hubspot',
    icon: hubspotAppIcon,
  },
  {
    name: 'insightly',
    icon: insightlyAppIcon,
  },
  {
    name: 'instagram messenger',
    icon: instagramAppIcon,
  },
  {
    name: 'jotform',
    icon: jotFormIcon,
  },
  {
    name: 'mailchimp',
    icon: mailchimpIcon,
  },
  {
    name: 'myob',
    icon: myobAppIcon,
  },
  {
    name: 'quickbooks app',
    icon: quickbooksAppIcon,
  },
  {
    name: 'quickbooks desktop',
    icon: quickbooksDesktopIcon,
  },
  {
    name: 'ringcentral',
    icon: ringCentralIcon,
  },
  {
    name: 'shopify',
    icon: shopifyAppIcon,
  },
  {
    name: 'app-smartdataimporter',
    icon: smartDataImporterIcon,
  },
  {
    name: 'twilio app',
    icon: twillioAppIcon,
  },
  {
    name: 'web chat app',
    icon: webchatAppIcon,
  },
  {
    name: 'facebook business messenger app',
    icon: facebookMessengerIconApp,
  },
  {
    name: 'typeform app',
    icon: typeFormIcon,
  },
  {
    name: 'starter growth package',
    icon: growthPackageIcon,
  },
  {
    name: 'expanded growth package',
    icon: growthPackageIcon,
  },
  {
    name: 'restricted access',
    icon: restrictedAccessIcon,
  },
  {
    name: 'gmail',
    icon: gmailLogo,
  },
  {
    name: 'thryv add-ons',
    icon: reverseOrangePlusIcon,
  },
  {
    // FULSAS-12283: covers "Visibility Boost", "Visibility Boost - Social
    // Posts", and "Visibility Boost - Blog Creation" via substring match.
    // Reuses the Social Ads icon as a placeholder until Product supplies a
    // dedicated Visibility Boost icon.
    name: 'visibility boost',
    icon: socialAdsIcon,
  },
  {
    // FULSAS-12281: real product name for the Thryv 2.0 Visibility Boost
    // "Blog Creation" entitlement (ent-5-mc-blog-creation) doesn't contain
    // "visibility boost", so it needs its own entry or it renders a broken
    // image. Reuses the Social Ads icon as a placeholder, same as above,
    // until Product supplies a dedicated icon.
    name: 'blog creation',
    icon: socialAdsIcon,
  },
  {
    name: 'logo',
    icon: logoBuilderIcon,
  },
  {
    name: 'custom video',
    icon: customVideoShootIcon,
  },
  {
    name: 'listings management',
    icon: listingsAppIcon,
  },
  {
    name: 'seo keywords',
    icon: seoKeywordsIcon,
  },
  {
    name: 'seo powerboost',
    icon: seoPowerboostIcon,
  },
  {
    name: 'marketing pro editor',
    icon: marketingProEditorIcon,
  },
  {
    name: 'thryv professional services',
    icon: professionalServiceIcon,
  },
  {
    name: 'social ads bronze',
    icon: socialAdsIcon,
  },
  {
    name: 'social ads silver',
    icon: socialAdsIcon,
  },
  {
    name: 'social ads gold',
    icon: socialAdsIcon,
  },
  {
    name: 'social ads bni',
    icon: socialAdsIcon,
  },
  {
    name: 'thryvpay',
    icon: thryvPayLogov1,
  },
  {
    name: 'zapier',
    icon: zapierPromotionIcon,
  },
  {
    name: 'thryv social posts',
    icon: ThryvSocialPostsIcon,
  },
  {
    name: 'silver service package',
    icon: supportV2Icon,
  },
  {
    name: 'platinum standard service package',
    icon: supportV2Icon,
  },
  {
    name: 'platinum premium service package',
    icon: supportV2Icon,
  },
  {
    name: 'platinum plus service package',
    icon: supportV2Icon,
  },
  {
    name: 'limited e-commerce crm import',
    icon: connectIcon,
  },
  {
    name: 'keap text & voice',
    icon: textingIcon,
  },
  {
    name: 'keap gmail standard',
    icon: keapMaxIcon,
  },
  {
    name: 'keap business line',
    icon: phoneMessageIcon,
  },
  {
    name: 'additional user',
    icon: profileIcon,
  },
  {
    name: 'additional contacts',
    icon: manageContactsIcon,
  },
  {
    name: 'hourly data service',
    icon: oneHourIcon,
  },
  {
    name: 'gold service package',
    icon: supportV2Icon,
  },
  {
    name: 'e-commerce data transfer',
    icon: connectIcon,
  },
  {
    name: 'e-commerce data import',
    icon: connectIcon,
  },
  {
    name: 'dedicated ip',
    icon: worldIcon,
  },
  {
    name: 'data restore',
    icon: uploadDocumentIcon,
  },
  {
    name: 'custom done for you',
    icon: complianceIcon,
  },
  {
    name: 'credit card import',
    icon: connectIcon,
  },
  {
    name: 'convrrt',
    icon: diyWebsiteIcon,
  },
  {
    name: 'basic crm data import',
    icon: connectIcon,
  },
  {
    name: 'ask the expert',
    icon: videoSupportIcon,
  },
  {
    name: 'api calls - 500k/month',
    icon: digitalThryvToolsIcon,
  },
  {
    name: 'advanced data transfer',
    icon: connectIcon,
  },
  {
    name: 'advanced crm data import',
    icon: connectIcon,
  },
  {
    name: '500 additional message bundle',
    icon: messagingv2Icon,
  },
  {
    name: 'keap add-ons',
    icon: reverseGreenPlusIcon,
  },
  {
    name: 'thryv 24x7 support and service fee',
    icon: supportCenter,
  },
  {
    name: 'quick launch',
    icon: keapQuickLaunchIcon,
  },
  {
    name: 'starter (monthly)',
    icon: thryvGrowthPlatformIcon,
  },
  {
    name: 'signature (monthly)',
    icon: thryvGrowthPlatformIcon,
  },
  {
    name: 'premier (monthly)',
    icon: thryvGrowthPlatformIcon,
  },
  {
    name: 'starter (annual)',
    icon: thryvGrowthPlatformIcon,
  },
  {
    name: 'signature (annual)',
    icon: thryvGrowthPlatformIcon,
  },
  {
    name: 'premier (annual)',
    icon: thryvGrowthPlatformIcon,
  },
  {
    name: 'listing refresh',
    icon: listingRefreshIcon,
  },
  {
    name: 'strategic content creation',
    icon: strategicContentCreationIcon,
  },
  {
    name: 'kcc certification',
    icon: keapMaxIcon,
  },
  {
    name: 'kcp certification',
    icon: keapMaxIcon,
  },
  {
    name: 'coach builder',
    icon: keapMaxIcon,
  },
  {
    name: 'priority hosting',
    icon: worldIcon,
  },
  {
    name: 'priority database',
    icon: digitalThryvToolsIcon,
  },
  {
    name: 'graphly',
    icon: manageContactsIcon,
  },
  {
    name: 'plusthis',
    icon: plusThisIcon,
  },
  {
    name: 'call tracking number',
    icon: callTrackingNumberIcon,
  },
  {
    name: 'geo add-on',
    icon: geoAddOnIcon,
  },
  {
    name: 'thryv geo expansion',
    icon: geoAddOnIcon,
  },
]

const HIPAA_COMPLIANT_INDUSTRIES = [
  '52k10f2ml8m1f41u', // dentists
  'pcqjcmfqjvknm31t', // health & wellness
  'e8cgl2m7jp221sx7', // healthcare providers
]

const DEFAULT_PAYMENT_TYPE = {
  value: '',
}
const DEFAULT_ONBOARDING_REQUIRED = {
  value: '',
}
const DEFAULT_ONBOARDING_FEE_REQUIRED = {
  value: '',
}
const DEFAULT_INCLUDE_ONBOARDING = {
  value: 'No',
}
const DEFAULT_MC_CONFIG = {
  isValid: false,
  value: '',
  shouldValidate: false,
  hideDomain: false,
}

const DEFAULT_SEO_CONFIG = {
  isValid: false,
  value: '',
}

const DEFAULT_KEAP_CONFIG = {
  isValid: false,
  value: '',
}

const DEFAULT_HVAC_CONFIG = {
  isValid: false,
  value: '',
}

const WEBSITE_5_CODES = ['addon-5-website_difm', 'ent-5-website']

const formatItemOnRemoval = (item, selectedCountry, iconName, groupTitle) => {
  const {unitAmount: amount} = findUnitAmount(
    item.currencies,
    selectedCountry,
    null,
  )
  const baseData = {
    code: item.code,
    name: item.name,
    id: item.id,
    quantity: 1,
    unitAmount: amount,
    amount: amount,
    img: iconName,
    groupTitle: groupTitle,
  }
  return baseData
}

const findUnitAmount = (currencies, selectedCountry, seatQty) => {
  const currency = currencies.find(
    currency =>
      currency.currency === CC_CURRENCIES[COUNTRIES[selectedCountry]?.value],
  )
  const unitAmount = currency?.unitAmount
  const needFlag = currency?.needFlag
  if (seatQty) {
    return {unitAmount: unitAmount * seatQty, needFlag}
  }
  return {unitAmount, needFlag}
}

const getPlanIcon = name => {
  const planItem = PLANS_ICONS.filter(item =>
    name.toLowerCase()?.includes(item.name),
  )
  return planItem[0]?.icon
}

// Adds `img` on every item (and nested addons) using your existing getPlanIcon
const attachImgsToConfigPayload = payload => {
  const withImg = item => ({
    ...item,
    img: getPlanIcon(item.iconKey || item.name),
    ...(item.addons
      ? {
          addons: item.addons.map(add => ({
            ...add,
            img: getPlanIcon(add.iconKey || add.name),
          })),
        }
      : {}),
  })

  return {
    centers: Array.isArray(payload?.centers)
      ? payload.centers.map(withImg)
      : [],
    apps: Array.isArray(payload?.apps) ? payload.apps.map(withImg) : [],
    oneTimes: Array.isArray(payload?.oneTimes)
      ? payload.oneTimes.map(withImg)
      : [],
  }
}

const findTierCurrencies = (tiers, seatQty) => {
  let tier
  tiers?.forEach(t => {
    if (
      t.endingQuantity >= seatQty &&
      (!tier || t.endingQuantity < tier.endingQuantity)
    ) {
      tier = t
    }
  })
  return tier?.currencies
}

const findTier = (tierCurrencies, selectedCountry) => {
  return tierCurrencies?.find(
    currency =>
      currency.currency === CC_CURRENCIES[COUNTRIES[selectedCountry]?.value],
  )
}

const formatItem = (item, selectedCountry, itemType, tierSettings) => {
  const tierCurrencies = findTierCurrencies(
    tierSettings?.tiers,
    tierSettings?.seatQty,
  )
  const {unitAmount: amount, needFlag} = findUnitAmount(
    tierCurrencies ? tierCurrencies : item.currencies,
    selectedCountry,
    tierCurrencies ? tierSettings?.seatQty : null,
  )
  const tier = findTier(tierCurrencies, selectedCountry)
  const normalizedRequires =
    item?.requires?.length > 0 ? item.requires : item?.requiresCenter || []

  // ES/IT/IN/PH/VN are Keap-only markets, same as GB — but GB gets that
  // restriction "for free" because product_price only has GBP rows for
  // Keap-family products. These 5 keep USD pricing, so mirror GB's
  // availability by checking for a GBP row on the same product, rather
  // than adding new country data — a product becomes available here
  // exactly when it's available in GB.
  const hasGbpPrice = item.currencies?.some(
    currency => currency.currency === 'GBP',
  )
  const isKeapOnlyRestricted =
    KEAP_ONLY_COUNTRIES.includes(selectedCountry) && !hasGbpPrice

  const baseData = {
    code: item.code,
    name: item.name,
    id: item.id,
    isRequired: item.isRequired,
    groupTitle: item.uigroup,
    groupSeq: item.groupSequence,
    productSeq: item.productSequence,
    prodType: item.prod_type,
    productRank: item.productRank,
    productBillingType: item.productBillingType,
    amount,
    needFlag,
    parentProduct: item?.parentProduct,
    requires: normalizedRequires,
    ...(item?.requiresAddon && {requiresAddon: item.requiresAddon}),
    ...(item?.requiresFlags && {requiresFlags: item.requiresFlags}),
    ...(item?.restrictFlags && {restrictFlags: item.restrictFlags}),
    ...(item?.disableApps && {disableApps: item.disableApps}),
    ...(item?.disableCenters && {disableCenters: item.disableCenters}),
    ...(item?.downgrades && {downgrades: item.downgrades}),
    ...(item?.maxQty && {maxQty: item.maxQty}),
    ...(item?.alternatePlanCodes?.length && {
      alternatePlanCodes: item.alternatePlanCodes,
    }),
    ...(item?.upgradeGated && {upgradeGated: item.upgradeGated}),
    ...(item?.upgradeGatedCountries && {
      upgradeGatedCountries: item.upgradeGatedCountries,
    }),
    ...(item?.showWhenCartHas && {showWhenCartHas: item.showWhenCartHas}),
    ...(item?.hideWhenCartHas && {hideWhenCartHas: item.hideWhenCartHas}),
    ...((isNaN(amount) || isKeapOnlyRestricted) && {isNotAvailable: true}),
    ...(item?.autoAddCenters?.length
      ? {
          autoAddCenters: item.autoAddCenters.map(autoAddCenter => {
            const tiers = autoAddCenter?.addons?.[0]?.tiers
            return formatItem(
              autoAddCenter,
              selectedCountry,
              'center',
              autoAddCenter?.addons && {tiers, seatQty: 1},
            )
          }),
        }
      : {}),
  }
  if (itemType === 'otFee') {
    return {
      ...baseData,
      appliesTo: item.appliesTo,
      requires: normalizedRequires,
      img: getPlanIcon(item.name),
    }
  }
  if (itemType === 'ent') {
    return {
      ...baseData,
      ...(item?.excludes && {excludes: item.excludes}),
    }
  }
  return {
    ...baseData,
    entitlements: item.entitlements?.filter(
      entitlement => !entitlement.isNotAvailable,
    ),
    requiresCenter: item.requiresCenter,
    excludes: item.excludes,
    ...(item?.addons && {tier}),
    img: getPlanIcon(item.name),
  }
}

function Catalog({
  nextStep,
  prevStep,
  selectedCountry,
  CCAccountInfo,
  industryTemplate,
  prevCentersAndApps,
  prevCode,
  setAccountInfo,
  setAccountSubscriptionData,
  accountSubscriptionData,
  accountInfo,
  setCurrentStep,
  order,
  setOrder,
  defaultUpgradeCentersAndApps,
  defaultExpiredCentersAndApps,
  all5PlanPrices,
  isEaidFound,
  setIsEaidFound,
  cohortEaid,
  setCohortEaid,
  setPreviewRequestFinished,
  setIsInvoiceBilled,
  setAllowInvoice,
  updateAddressInfo,
  allowInvoice,
  isCohort,
  setIsCohort,
  isNewAccountOrderCreated,
  orderData5CU,
  setOrderData5CU,
  captureOrder5NewUpgrade,
  setOrder5LinesDiscounts,
  isWebsiteValid,
  setIsWebsiteValid,
  isNewTLMCAccessible,
  hasKeapAccountAbility,
  setHasKeapAccountAbility,
  keapAccount,
  setKeapAccount,
  keapID,
  setKeapID,
  isBillingNoticeDisplayed,
  setIsBillingNoticeDisplayed,
  setShowErrorAccountModal,
  setShowErrorSubscriptionModal,
  navFrom,
  keapTrailAppId,
  setKeapTrailAppId,
  setKeapTextVoiceQty,
  setKeapTextVoiceQtyAnnual,
  setKeapAddContactsQty,
  setKeapAddContactsQtyAnnual,
  setKeapAddContactsQtyGraphlyAnnual,
  setPlusThisPremiumQtyGraphlyAnnual,
  setKeapAddContactsQtyGraphlyM2m,
  setPlusThisPremiumQtyGraphlyM2m,
  prefetchedAccountSubscriptions,
  prefetchedDefaultConfig,
}) {
  const [plans, setPlans] = useState(null)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [selectedCentersAndApps, setSelectedCentersAndApps] = useState({
    centers: [],
    apps: [],
    oneTimes: [],
  })
  const couponDispatch = useDispatch()
  const ccCouponInfo = useSelector(selectManualCoupons, shallowEqual)
  const upgradePaymentPreview = useSelector(selectUpgradePaymentPreview)

  // const [subsCodeList, setSubsCodeList] = useState([])
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [selectedOptions, setSelectedOptions] = useState([])
  const [proposalId, setProposalId] = useState('')
  const [proposalValidation, setProposalValidation] = useState(null)
  // const [keapAccount, setKeapAccount] = useState(null)
  const [selectedAdditionalContactOption, setSelectedAdditionalContactOption] =
    useState({
      name: '',
      value: '',
    })
  const [
    selectedAdditionalContactOptionAnnual,
    setSelectedAdditionalContactOptionAnnual,
  ] = useState({
    name: '',
    value: '',
  })
  const [
    selectedAdditionalContactOptionGraphlyAnnual,
    setSelectedAdditionalContactOptionGraphlyAnnual,
  ] = useState({
    name: '',
    value: '',
  })
  const [selectedPlusThisPremiumAnnual, setSelectedPlusThisPremiumAnnual] =
    useState({
      name: '',
      value: '',
    })
  const [
    selectedAdditionalContactOptionGraphlyM2m,
    setSelectedAdditionalContactOptionGraphlyM2m,
  ] = useState({
    name: '',
    value: '',
  })
  const [selectedPlusThisPremiumM2m, setSelectedPlusThisPremiumM2m] = useState({
    name: '',
    value: '',
  })
  const [selectedKeapTextVoice, setSelectedKeapTextVoice] = useState({
    name: '',
    value: '',
  })
  const [selectedKeapTextVoiceAnnual, setSelectedKeapTextVoiceAnnual] =
    useState({
      name: '',
      value: '',
    })
  const [selectedKcpOption, setSelectedKcpOption] = useState({
    name: '',
    value: '',
  })
  const [alphaSEOFromRecurly, setAlphaSEOFromRecurly] = useState({
    alphaProposalId: '',
    alphaDomain: '',
    alphaAmount: 0,
    // alphaCategories: '',
    // alphaGeos: '',
    alphaKeyCount: 0,
    alphaPowerboostCount: 0,
    alphaIsExternalDomain: null,
    alphaStartDate: null,
  })
  const [alphaProposalId, setAlphaProposalId] = useState('')
  const [alphaDomain, setAlphaDomain] = useState('')
  const [alphaAmount, setAlphaAmount] = useState(null)
  // FULSAS-12507: domain already on file for the account's Thryv 2.0 (MC 2.0)
  // subscription, read from its Recurly "Domain" custom field - used to
  // pre-populate the Leads Lift domain field when that add-on is selected.
  const [existingMc2DomainValue, setExistingMc2DomainValue] = useState('')
  // const [categories, setCategories] = useState('')
  // const [geos, setGeos] = useState('')
  const [isExternalDomain, setIsExternalDomain] = useState(null)
  const [alphaKeyCount, setAlphaKeyCount] = useState(null)
  const [alphaPowerboostCount, setAlphaPowerboostCount] = useState(null)
  const [paymentType, setPaymentType] = useState(DEFAULT_PAYMENT_TYPE)
  const [onboardingRequired, setOnboardingRequired] = useState(
    DEFAULT_ONBOARDING_REQUIRED,
  )
  const [onboardingFeeRequired, setOnboardingFeeRequired] = useState(
    DEFAULT_ONBOARDING_FEE_REQUIRED,
  )
  const [includeOnboarding, setIncludeOnboarding] = useState(
    DEFAULT_INCLUDE_ONBOARDING,
  )
  const [thryvLeadsId, setThryvLeadsId] = useState('')
  const [thryvLeadsValidation, setThryvLeadsValidation] = useState(null)
  const [mcDomainConfig, setMcDomainConfig] = useState(DEFAULT_MC_CONFIG)
  const [tlDomainConfig, setTlDomainConfig] = useState(DEFAULT_MC_CONFIG)
  const [llDomainConfig, setLlDomainConfig] = useState(DEFAULT_MC_CONFIG)
  const [keapDomainConfig, setKeapDomainConfig] = useState(DEFAULT_KEAP_CONFIG)
  const [seoDomainConfig, setSeoDomainConfig] = useState(DEFAULT_SEO_CONFIG)
  const [hvacDomainConfig, setHvacDomainConfig] = useState(DEFAULT_HVAC_CONFIG)
  const [futureBillingDate, setFutureBillingDate] = useState()
  const [sensisSiteDomain, setSensisSiteDomain] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [totalPrice, setTotalPrice] = useState('$0.00')
  const [isSEOMBGDisabled, setIsSEOMBGDisabled] = useState(false)
  const [accountSubscriptions, setAccountSubscriptions] = useState(null)
  const [isWebsiteSettingsModalVisible, setIsWebsiteSettingsModalVisible] =
    useState(false)
  const [showUpdateAddressModal, setShowUpdateAddressModal] = useState(false)
  const [monthlyTotal, setMonthlyTotal] = useState(0)
  // const defaultCentersAndApps = useRef({})
  // const expiredCentersAndApps = useRef({})
  const defaultCentersAndApps = useRef({centers: [], apps: [], oneTimes: []})
  const expiredCentersAndApps = useRef({centers: [], apps: [], oneTimes: []})
  const [alternatePlanCodesToHide, setAlternatePlanCodesToHide] = useState(
    new Set(),
  )
  const [seatQty, setSeatQty] = useState(0)
  const [isLegacyUpgradeAllowed, setIsLegacyUpgradeAllowed] = useState(false)
  const [retainWebsiteModalState, setRetainWebsiteModalState] = useState(
    RETAIN_WEBSITE_MODAL_STATES.CLOSE,
  )
  const [mc20WebsiteDowngradeModalState, setMc20WebsiteDowngradeModalState] =
    useState(RETAIN_WEBSITE_MODAL_STATES.CLOSE)
  const [mc20WebsiteDowngradePlanName, setMc20WebsiteDowngradePlanName] =
    useState('')
  const [keapTextVoiceRecurlyQty, setKeapTextVoiceRecurlyQty] = useState(null)
  const [keapTextVoiceRecurlyQtyAnnual, setKeapTextVoiceRecurlyQtyAnnual] =
    useState(null)

  const [keapAddContactsRecurlyQty, setKeapAddContactsRecurlyQty] =
    useState(null)
  const [keapAddContactsRecurlyQtyAnnual, setKeapAddContactsRecurlyQtyAnnual] =
    useState(null)

  const [keapAddUsersRecurlyQty, setKeapAddUsersRecurlyQty] = useState(0)
  const [keapAddUsersRecurlyQtyAnnual, setKeapAddUsersRecurlyQtyAnnual] =
    useState(0)

  const [plusThisPremiumRecurlyQtyAnnual, setPlusThisPremiumRecurlyQtyAnnual] =
    useState(null)
  const [
    keapAddContactsRecurlyQtyGraphlyAnnual,
    setKeapAddContactsRecurlyQtyGraphlyAnnual,
  ] = useState(null)
  const [
    keapAddContactsRecurlyQtyGraphlyM2m,
    setKeapAddContactsRecurlyQtyGraphlyM2m,
  ] = useState(null)
  const [plusThisPremiumRecurlyQtyM2m, setPlusThisPremiumRecurlyQtyM2m] =
    useState(null)

  const [kcpRecurlyOption, setKcpRecurlyOption] = useState(null)
  const [kccRecurlyOption, setKccRecurlyOption] = useState(0)

  const [isFreeTrailCustomer, setIsFreeTrailCustomer] = useState(null)
  const client = useAuthClient()

  const {pathname} = useLocation()

  const KEAP_FAMILY_M2M_CODES = useMemo(
    () =>
      all5PlanPrices?.centers
        .filter(
          center =>
            center.uigroup === 'Keap' &&
            center.productBillingType != 'Annual' &&
            center.code.startsWith('keap_'),
        )
        .map(center => center.code),
    [all5PlanPrices?.centers],
  )
  const KEAP_FAMILY_LEGACY_M2M_CODES = useMemo(
    () =>
      all5PlanPrices?.centers
        .filter(
          center =>
            center.uigroup === 'Hidden' &&
            center.productBillingType === 'Monthly' &&
            center.code.startsWith('keap_'),
        )
        .map(center => center.code),
    [all5PlanPrices?.centers],
  )
  const KEAP_FAMILY_ANNUAL_CODES = useMemo(
    () =>
      all5PlanPrices?.centers
        .filter(
          center =>
            center.uigroup === 'Keap' &&
            center.productBillingType === 'Annual' &&
            center.code.startsWith('keap_'),
        )
        .map(center => center.code),
    [all5PlanPrices?.centers],
  )
  const KEAP_FAMILY_LEGACY_ANNUAL_CODES = useMemo(
    () =>
      all5PlanPrices?.centers
        .filter(
          center =>
            center.uigroup === 'Hidden' &&
            center.productBillingType === 'Annual' &&
            center.code.startsWith('keap_'),
        )
        .map(center => center.code),
    [all5PlanPrices?.centers],
  )

  let KCP_OPTIONS =
    selectedCountry === 'US'
      ? [
          {name: '$1500', value: '1500'},
          {name: '$750', value: '750'},
          {name: '$499', value: '499'},
          {name: '$299', value: '299'},
        ]
      : selectedCountry === 'CA'
      ? [
          {name: '$405', value: '405'},
          {name: '$700', value: '700'},
          {name: '$1000', value: '1000'},
          {name: '$2000', value: '2000'},
        ]
      : selectedCountry === 'AU'
      ? [
          {name: '$418', value: '418'},
          {name: '$704', value: '704'},
          {name: '$1001', value: '1001'},
          {name: '$2002', value: '2002'},
        ]
      : selectedCountry === 'NZ'
      ? [
          {name: '$462', value: '462'},
          {name: '$770', value: '770'},
          {name: '$1100', value: '1100'},
          {name: '$2200', value: '2200'},
        ]
      : [
          {name: '£223', value: '223'},
          {name: '£372', value: '372'},
          {name: '£559', value: '559'},
          {name: '£1116', value: '1116'},
        ]

  const {
    BC_Catalog_Provisions_Pilot,
    BC_Catalog_Restrictions,
    MC_Catalog_Provisions_Pilot,
    MC_Catalog_Restrictions,
    KeapMax_Catalog_Provisions: hasKeapMaxAccess,
    ThryvHVAC_Catalog_Provisions: hasKeapHVACAccess,
    BC_Catalog_Update_Only,
    Custom_Data_For_You_Access: hasCustomDataFYAccess,
    // Custom_Data_Service_Access: hasCustomDataServiceAccess,
    Enable_BNI_Mktg_Svcs,
  } = useUserFeatures()

  const hasBusinessCenterAccess = useMemo(() => {
    const isBcInDefaultCenters = defaultCentersAndApps?.current?.centers?.some(
      ({code}) => code.startsWith('bc_'),
    )
    if (BC_Catalog_Update_Only && !isBcInDefaultCenters) return false
    if (!BC_Catalog_Provisions_Pilot && !BC_Catalog_Restrictions) {
      return true
    }
    if (BC_Catalog_Restrictions) return false
    if (BC_Catalog_Provisions_Pilot) return true
    return true
  }, [
    BC_Catalog_Restrictions,
    BC_Catalog_Provisions_Pilot,
    BC_Catalog_Update_Only,
    defaultCentersAndApps?.currrent?.centers,
  ])

  const hasMarketingCenterAccess = useMemo(() => {
    if (!MC_Catalog_Provisions_Pilot && !MC_Catalog_Restrictions) {
      return true
    }
    if (MC_Catalog_Restrictions) return false
    if (MC_Catalog_Provisions_Pilot) return true
    return true
  }, [MC_Catalog_Provisions_Pilot, MC_Catalog_Restrictions])

  const isUpgrade = pathname === '/order/upgrade'
  const isNewOrder = pathname === '/order/new'
  const showHipaa = HIPAA_COMPLIANT_INDUSTRIES.includes(industryTemplate)
  const getAccountCentersAndApps = allSubscriptions => {
    const STARTER_PACKAGE_ADDON_CODES = [
      'addon-5-mcstartgr',
      'addon-5-mcexpangr',
    ]
    let centersAndApps = {centers: [], apps: []}
    let thryvMaxAddonprods = all5PlanPrices.apps
      .filter(
        app =>
          app.parentProduct === 'addon-5-keap-addons' &&
          app.code !== 'addon-5-keap-addons',
      )
      .map(item => item.code)

    allSubscriptions.forEach(subscription => {
      let found = false
      if (subscription?.code === 'mc_foundation-5-thryv-m2m') {
        const formattedCenter = {
          code: 'mc_foundation-5-thryv-m2m',
          name: 'Marketing Center Foundation',
          id: 'marketingFound',
          entitlements: [],
          amount: 0,
          uuid: subscription.uuid,
          unitAmount: subscription.unitAmount,
          quantity: subscription.quantity,
          groupTitle: 'Marketing Center',
          createdAt: subscription.createdAt,
          img: marketingCenterIconv1,
          ...(isUpgrade && {alreadyIncluded: true}),
        }
        centersAndApps.centers.push(formattedCenter)
        found = true
      } else if (subscription?.code.startsWith('cc_')) {
        const entitlements = subscription?.addons.map(addon => {
          return {
            ...addon,
            amount:
              addon.unitAmount && addon.quantity
                ? addon.unitAmount * addon.quantity
                : 0,
            alreadyIncluded: true,
          }
        })
        const formattedCenter = {
          code: subscription.code,
          name: COMMAND_CENTER_NAME_MAPPING[subscription.code],
          id: subscription.code,
          entitlements: entitlements,
          amount: subscription.total,
          uuid: subscription.uuid,
          unitAmount: subscription.unitAmount,
          quantity: subscription.quantity,
          createdAt: subscription.createdAt,
          addons: subscription.addons,
          groupTitle: 'Command Center',
          img: commandCenterIconV1,
          ...(isUpgrade && {alreadyIncluded: true}),
        }
        centersAndApps.centers.push(formattedCenter)
        found = true
      } else {
        for (const center of plans.centers) {
          if (subscription.code === center.code) {
            let isGrowthPackageInCart = subscription.addons?.some(({code}) =>
              STARTER_PACKAGE_ADDON_CODES.includes(code),
            )
            const startDateFromSubscription =
              subscription.startDate ||
              subscription.start_date ||
              getCustomFieldValue(subscription?.customFields, [
                'futureBillingDate',
                'Future_Billing_Date',
                'future_billing_date',
                'startDate',
                'start_date',
              ])
            const entitlements =
              subscription.code === 'seobase-5-thryv-m2m'
                ? []
                : subscription.addons
                    .filter(addon =>
                      center.entitlements.some(({code}) => code === addon.code),
                    )
                    .map(addon => {
                      const excludes = center.entitlements.find(
                        ({code}) => code === addon.code,
                      )?.excludes
                      return {
                        ...addon,
                        amount:
                          addon.unitAmount && addon.quantity
                            ? addon.unitAmount * addon.quantity
                            : 0,
                        alreadyIncluded: true,
                        // recurly_subscription_id: addon.subscriptionId,
                        excludes,
                      }
                    })
            const formattedCenter = {
              ...center,
              entitlements,
              groupTitle: subscription.groupTitle,
              uuid: subscription.uuid,
              unitAmount: subscription.unitAmount,
              quantity: subscription.quantity,
              createdAt: subscription.createdAt,
              ...(startDateFromSubscription && {
                startDate: dayjs(startDateFromSubscription).format(
                  'YYYY-MM-DD hh:mm:ss',
                ),
              }),
              ...(isUpgrade && {alreadyIncluded: true}),
            }
            const isSEOorLeads =
              (subscription.code.includes('seo') &&
                subscription.code !== 'seobase-5-thryv-m2m') ||
              [
                'tl-mc-5-thryv-m2m',
                'tl-tax-5-thryv-m2m',
                'tl-5-thryv-m2m',
              ].includes(subscription.code)
            if (isSEOorLeads) {
              formattedCenter.amount = subscription.quantity
            }
            if (subscription.code === 'seobase-5-thryv-m2m') {
              formattedCenter.amount = subscription.quantity
              formattedCenter.quantity = 1
            }
            if (subscription.code === 'keap_kcp-cert-5-fee-annual') {
              formattedCenter.amount = subscription.unitAmount
              formattedCenter.quantity = subscription.quantity
            }
            if (subscription.code === 'keap_kcc-cert-5-fee-annual') {
              formattedCenter.amount = subscription.unitAmount
              subscription.amount = subscription.unitAmount
            }
            if (isGrowthPackageInCart) {
              const growthPackageAddOn = subscription.addons?.find(
                ({code}) =>
                  code === 'addon-5-mcstartgr' || code === 'addon-5-mcexpangr',
              )
              growthPackageAddOn.img = getPlanIcon(growthPackageAddOn.name)
              growthPackageAddOn.amount = growthPackageAddOn.unitAmount
              // growthPackageAddOn.recurly_subscription_id =
              //   growthPackageAddOn.subscriptionId
              centersAndApps.apps.push(growthPackageAddOn)
            }
            centersAndApps.centers.push(formattedCenter)
            found = true
            break
          }
        }
        if (!found) {
          for (const app of plans.apps) {
            if (subscription.code === app.code) {
              const unitAmt =
                subscription.unitAmount ?? subscription.unit_amount
              const formattedApp = {
                ...app,
                uuid: subscription.uuid,
                unitAmount: unitAmt,
                groupTitle: subscription.groupTitle,
                quantity: subscription.quantity,
                ...(isUpgrade && {alreadyIncluded: true}),
                // Quantity-based addons: amount must be unit price for formatPrice/calcTotal
                ...((app.code === 'addon-5-keap-addusers-m2m' ||
                  app.code === 'addon-5-keap-addusers-annual' ||
                  app.code === 'addon-ctn-bninat-m2m' ||
                  app.code === 'addon-ctn-bninat-annual') &&
                  unitAmt != null && {amount: unitAmt}),
              }
              // addon-5-keap-addons parent displays as "Keap Add-Ons" (consistent with options, order-summary)
              if (
                app.code === 'addon-5-keap-addons' ||
                app.code === 'addon-5-keap-addons-annual'
              ) {
                formattedApp.name = 'Keap Add-Ons'
                formattedApp.groupTitle = 'Keap Add-Ons'
              }
              const isSEOorLeads =
                (app.code.includes('seo') &&
                  app.code !== 'seobase-5-thryv-m2m') ||
                [
                  'tl-mc-5-thryv-m2m',
                  'tl-tax-5-thryv-m2m',
                  'tl-5-thryv-m2m',
                ].includes(app.code)
              if (isSEOorLeads) {
                formattedApp.amount = subscription.quantity
              }
              if (app.code === 'seobase-5-thryv-m2m') {
                formattedApp.amount = subscription.quantity
                formattedApp.quantity = 1
                formattedApp.requiresCenter = app.requiresCenter
              }
              centersAndApps.apps.push(formattedApp)
              found = true
              break
            }
          }
        }
        if (!found) {
          subscription['alreadyIncluded'] = true
          subscription.img = getPlanIcon(subscription.name)
          const isSEOorLeads =
            (subscription.code.includes('seo') &&
              subscription.code !== 'seobase-5-thryv-m2m') ||
            [
              'tl-mc-5-thryv-m2m',
              'tl-tax-5-thryv-m2m',
              'tl-5-thryv-m2m',
            ].includes(subscription.code)
          if (isSEOorLeads) {
            subscription.amount = subscription.quantity
          }
          if (subscription.code === 'seobase-5-thryv-m2m') {
            subscription.amount = subscription.quantity
            subscription.quantity = 1
          }
          if (
            subscription.code === 'addon-5-keap-addons' ||
            subscription.code === 'addon-5-keap-addons-annual'
          ) {
            subscription.name = 'Keap Add-Ons'
            subscription.groupTitle = 'Keap Add-Ons'
            subscription.img = getPlanIcon('Keap Add-Ons')
            subscription.amount = subscription.unitAmount
              ? subscription.subtotal
              : 0
          } else if (
            subscription.code === 'addon-5-seokey-m2m' ||
            subscription.code === 'addon-5-seopb-m2m' ||
            subscription.code === 'addon-6-seats-m2m'
          ) {
            // subscription['alreadyIncluded'] = true
            subscription['groupTitle'] = 'Thryv Add-Ons'
            if (subscription.code === 'addon-5-seokey-m2m')
              subscription.img = seoKeywordsIcon
            if (subscription.code === 'addon-5-seopb-m2m')
              subscription.img = seoPowerboostIcon
            subscription.amount = subscription.unitAmount
          } else if (SOCIAL_ADS_BNI_PLAN_CODES.includes(subscription.code)) {
            if (subscription.code.endsWith('-annual')) {
              // GEP-6860: Annual BNI plans are provisioned by Salesforce —
              // hide completely from catalog UI (catalog, order summary, payment link).
              // return exits this forEach iteration without pushing to centersAndApps.apps.
              return
            }
            // GEP-6803: Monthly BNI is read-only display; provisioned in Salesforce →
            // Force amount=0 so it does not contribute to the catalog Total.
            subscription['groupTitle'] = 'Thryv Add-Ons'
            subscription.amount = subscription.unitAmount ?? 0
          } else {
            if (
              subscription.code === 'appmarket-5-thryv-m2m' ||
              subscription.code === 'addon-5-signatures_usage' ||
              subscription.code === 'app-websitebuilder' ||
              thryvMaxAddonprods?.includes(subscription.code)
            ) {
              // subscription['alreadyIncluded'] = true
              if (
                subscription.code === 'addon-5-signatures_usage' ||
                subscription.code === 'app-websitebuilder'
              ) {
                subscription['groupTitle'] = 'Thryv Add-Ons'
              }
              if (thryvMaxAddonprods?.includes(subscription.code)) {
                subscription['groupTitle'] = 'Thryv Add-Ons'
              }
            }
            // Quantity-based addons: use unitAmount so formatPrice/calcTotal
            // can multiply by quantity without double-counting the subtotal.
            if (
              subscription.code === 'addon-5-keap-addusers-m2m' ||
              subscription.code === 'addon-5-keap-addusers-annual' ||
              subscription.code === 'addon-ctn-bninat-m2m' ||
              subscription.code === 'addon-ctn-bninat-annual'
            ) {
              subscription.amount = subscription.unitAmount ?? 0
            } else {
              subscription.amount = subscription.unitAmount
                ? subscription.subtotal
                : 0
            }
          }

          centersAndApps.apps.push(subscription)
        }
      }
    })

    // centersAndApps.apps = centersAndApps.apps.sort((item1, item2) => {
    //   let isItem1MaxAddon =
    //     item1.code === 'addon-5-keap-addons' ||
    //     thryvMaxAddonprods.includes(item1.code)
    //   let isItem2MaxAddon =
    //     item2.code === 'addon-5-keap-addons' ||
    //     thryvMaxAddonprods.includes(item2.code)

    //   if (isItem1MaxAddon && !isItem2MaxAddon) return -1
    //   if (!isItem1MaxAddon && isItem2MaxAddon) return 1
    //   return 0
    // })

    if (!Enable_BNI_Mktg_Svcs) {
      centersAndApps.apps = centersAndApps.apps.filter(
        ({code}) =>
          ![
            'addon-5-mktgsvcs-ypcom-m2m',
            'addon-5-mktgsvcs-ypcom-annual',
            'addon-5-mktgsvcs-soci-m2m',
            'addon-5-mktgsvcs-soci-annual',
          ].includes(code),
      )
    }

    // Deduplicate apps by code — both `isGrowthPackageInCart` (center path) and
    // `...appMarketplace.addons` spread can push the same addon code independently.
    centersAndApps.apps = centersAndApps.apps.filter(
      (app, idx, arr) => arr.findIndex(a => a.code === app.code) === idx,
    )
    console.log('centersAndApps.apps ::::: ', centersAndApps.apps)
    defaultCentersAndApps.current = {...centersAndApps, oneTimes: []}
    if (isUpgrade) defaultUpgradeCentersAndApps.current = centersAndApps
    console.log('centersAndApps', centersAndApps)
    return centersAndApps
  }

  const getExpiredAccountCentersAndApps = expiredSubscriptions => {
    const STARTER_PACKAGE_ADDON_CODES = [
      'addon-5-mcstartgr',
      'addon-5-mcexpangr',
    ]
    let centersAndApps = {centers: [], apps: []}

    expiredSubscriptions.forEach(subscription => {
      let found = false
      for (const center of plans.centers) {
        if (subscription.code === center.code) {
          let isGrowthPackageInCart = subscription.addons?.some(({code}) =>
            STARTER_PACKAGE_ADDON_CODES.includes(code),
          )
          const entitlements =
            subscription.code === 'seobase-5-thryv-m2m'
              ? []
              : subscription.addons
                  .filter(addon =>
                    center.entitlements.some(({code}) => code === addon.code),
                  )
                  .map(addon => {
                    const excludes = center.entitlements.find(
                      ({code}) => code === addon.code,
                    )?.excludes
                    return {
                      ...addon,
                      amount:
                        addon.unitAmount && addon.quantity
                          ? addon.unitAmount * addon.quantity
                          : 0,
                      alreadyIncluded: true,
                      excludes,
                    }
                  })
          const formattedCenter = {
            ...center,
            entitlements,
            quantity: subscription.quantity,
            createdAt: subscription.createdAt,
            ...(isUpgrade && {alreadyIncluded: true}),
          }
          const isSEOorLeads =
            (subscription.code.includes('seo') &&
              subscription.code !== 'seobase-5-thryv-m2m') ||
            subscription.name === 'Thryv Leads'
          if (isSEOorLeads) {
            formattedCenter.amount = subscription.quantity
          }
          if (subscription.code === 'seobase-5-thryv-m2m') {
            formattedCenter.amount = subscription.quantity
            formattedCenter.quantity = 1
          }
          if (isGrowthPackageInCart) {
            const growthPackageAddOn = subscription.addons?.find(
              ({code}) =>
                code === 'addon-5-mcstartgr' || code === 'addon-5-mcexpangr',
            )
            growthPackageAddOn.img = getPlanIcon(growthPackageAddOn.name)
            growthPackageAddOn.amount = growthPackageAddOn.unitAmount
            centersAndApps.apps.push(growthPackageAddOn)
          }
          centersAndApps.centers.push(formattedCenter)
          found = true
          break
        }
      }
      if (!found) {
        for (const app of plans.apps) {
          if (subscription.code === app.code) {
            centersAndApps.apps.push({
              ...app,
              quantity: subscription.quantity,
              ...(isUpgrade && {alreadyIncluded: true}),
            })
            found = true
            break
          }
        }
      }
      if (!found) {
        subscription.img = getPlanIcon(subscription.name)
        if (
          subscription.code === 'addon-5-seokey-m2m' ||
          subscription.code === 'addon-5-seopb-m2m'
        ) {
          if (subscription.code === 'addon-5-seokey-m2m')
            subscription.img = seoKeywordsIcon
          if (subscription.code === 'addon-5-seopb-m2m')
            subscription.img = seoPowerboostIcon
          subscription.amount = subscription.unitAmount
        } else {
          subscription.amount = subscription.unitAmount
            ? subscription.subtotal
            : 0
        }

        centersAndApps.apps.push(subscription)
      }
    })

    expiredCentersAndApps.current = {...centersAndApps, oneTimes: []}
    if (isUpgrade) defaultExpiredCentersAndApps.current = centersAndApps
    return expiredCentersAndApps.current
  }

  const formatSelectedCentersAndApps = useCallback(() => {
    if (!plans || !accountSubscriptions) return
    const {
      childSubscriptions,
      parentSubscriptions,
      ccSeats,
      expiredParentSubscriptions,
      expiredChildSubscriptions,
    } = accountSubscriptions

    setSeatQty(ccSeats > 0 ? ccSeats : 1)
    const appMarketplace = childSubscriptions.find(
      ({code}) => code === 'appmarket-5-thryv-m2m',
    )
    const alphSEOProd = childSubscriptions.find(
      ({code}) => code === 'seobase-5-thryv-m2m',
    )
    const thryvMaxAddons = childSubscriptions.find(
      ({code}) => code === 'addon-5-keap-addons',
    )

    const thryvMaxAddonsAnnual = childSubscriptions.find(
      ({code}) => code === 'addon-5-keap-addons-annual',
    )

    const thryvMaxAddonsAnnualLegacy = childSubscriptions.find(
      ({code}) => code === 'addon-5-keap-addons-legacy-annual',
    )

    const thryvMaxAddonsLegacy = childSubscriptions.find(
      ({code}) => code === 'addon-5-keap-addons-legacy-m2m',
    )

    let allSubscriptions = [
      ...parentSubscriptions,
      ...childSubscriptions,
      ...appMarketplace.addons,
    ]

    if (alphSEOProd) {
      allSubscriptions = [...allSubscriptions, ...alphSEOProd.addons]
    }
    if (thryvMaxAddons) {
      allSubscriptions = [...allSubscriptions, ...thryvMaxAddons.addons]
    }
    if (thryvMaxAddonsAnnual) {
      allSubscriptions = [...allSubscriptions, ...thryvMaxAddonsAnnual.addons]
    }

    if (thryvMaxAddonsAnnualLegacy) {
      allSubscriptions = [
        ...allSubscriptions,
        ...thryvMaxAddonsAnnualLegacy.addons,
      ]
    }
    if (thryvMaxAddonsLegacy) {
      allSubscriptions = [...allSubscriptions, ...thryvMaxAddonsLegacy.addons]
    }
    // const allSubscriptions = alphSEOProd
    //   ? [
    //       ...parentSubscriptions,
    //       ...childSubscriptions,
    //       ...appMarketplace.addons,
    //       ...alphSEOProd.addons,
    //     ]
    //   : [
    //       ...parentSubscriptions,
    //       ...childSubscriptions,
    //       ...appMarketplace.addons,
    //     ]

    const expiredSubscriptions = [
      ...expiredParentSubscriptions,
      ...expiredChildSubscriptions,
    ]
    const formattedCentersAndApps = getAccountCentersAndApps(allSubscriptions)
    const dedupedPrev = prevCentersAndApps
      ? {
          ...prevCentersAndApps,
          apps: prevCentersAndApps.apps.filter(
            (a, idx, arr) => arr.findIndex(x => x.code === a.code) === idx,
          ),
        }
      : null
    setSelectedCentersAndApps(
      dedupedPrev ? dedupedPrev : {...formattedCentersAndApps, oneTimes: []},
    )
    getExpiredAccountCentersAndApps(expiredSubscriptions)
    if (prevCode) setCode(prevCode)
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevCentersAndApps, prevCode, plans, accountSubscriptions])

  const getPlansPrices = useCallback(() => {
    if (
      !all5PlanPrices ||
      (isUpgrade && !accountInfo.billingAddress?.country)
    ) {
      return
    }
    // Build base→alternate mapping from alternatePlanCodes on each center
    const baseToAlternateMap = new Map()
    const alternateToBaseMap = new Map()
    all5PlanPrices.centers.forEach(center => {
      center.alternatePlanCodes?.forEach(entry => {
        if (
          entry.swap_when_selected &&
          entry.alternate_plan_code !== center.code
        ) {
          baseToAlternateMap.set(center.code, entry.alternate_plan_code)
          alternateToBaseMap.set(entry.alternate_plan_code, center.code)
        }
      })
    })
    const customerCenterCodes = new Set(
      (defaultCentersAndApps.current?.centers ?? []).map(c => c.code),
    )
    // For each base/alternate pair, compute which plan code to hide from the UI
    const hiddenCodes = new Set()
    baseToAlternateMap.forEach((alternateCode, baseCode) => {
      if (isUpgrade && customerCenterCodes.has(baseCode)) {
        // Customer already owns the base plan → show base, hide alternate
        hiddenCodes.add(alternateCode)
      } else {
        // New order, or customer has alternate → show alternate, hide base
        hiddenCodes.add(baseCode)
      }
    })
    setAlternatePlanCodesToHide(hiddenCodes)

    const formattedCentersAndApps = {
      centers: all5PlanPrices.centers
        .map(center => {
          const tiers = center?.addons?.[0]?.tiers
          return formatItem(
            center,
            selectedCountry,
            'center',
            center?.addons && {tiers, seatQty: seatQty},
          )
        })
        .filter(center => !center.isNotAvailable)
        .sort((a, b) => {
          function rank(plan) {
            const planName = plan.name.toLowerCase()
            if (planName.includes('my account')) return 1
            if (planName.includes('business')) return 2
            if (planName.includes('marketing')) return 3
            if (planName.includes('reporting')) return 4
            if (planName.includes('workforce')) return 5
            return 6
          }
          const rankA = rank(a)
          const rankB = rank(b)
          if (rankA !== rankB) {
            return rankA - rankB
          } else {
            return a.amount - b.amount
          }
        }),
      apps: all5PlanPrices.apps
        .map(app => formatItem(app, selectedCountry, 'app'))
        .filter(
          app =>
            !app.isNotAvailable &&
            !(app.name === 'HIPAA' && !showHipaa) &&
            !(
              app.name === 'Restricted Access' &&
              showHipaa &&
              !['BB', 'KY'].includes(selectedCountry)
            ),
        )
        .sort((a, b) => {
          function rank(app) {
            const appName = app.name.toLowerCase()
            if (appName.includes('social ads')) return 1
            if (
              appName.includes('expanded growth package') ||
              appName.includes('starter growth package')
            ) {
              return 1
            }
            if (appName.includes('gmail')) {
              return 1.5
            }
            if (appName.includes('keap max')) {
              return 1.6
            }
            if (appName.includes('zapier')) {
              return 1.7
            }
            return 2
          }
          const rankA = rank(a)
          const rankB = rank(b)
          if (rankA !== rankB) {
            return rankA - rankB
          } else {
            return a.amount - b.amount
          }
        }),
      oneTimes: all5PlanPrices.oneTimeFees
        .map(fee => formatItem(fee, selectedCountry, 'otFee'))
        .filter(otFee => !otFee.isNotAvailable),
    }

    const includedEntitlements =
      defaultCentersAndApps.current?.centers?.flatMap(
        ({entitlements}) => entitlements,
      )
    const includedApps = defaultCentersAndApps.current?.apps?.map(
      ({code}) => code,
    )

    formattedCentersAndApps.centers.forEach(center => {
      center.entitlements = center.entitlements.map((entitlement, index) => {
        const isEntitlementIncluded = includedEntitlements?.some(
          ({code}) => code === entitlement.code,
        )
        const oneTimeFees = formattedCentersAndApps?.oneTimes.filter(
          oneTime => oneTime.appliesTo.length > 0,
        )
        const applicableOneTimes = oneTimeFees.filter(fee =>
          fee.appliesTo.some(
            rule =>
              rule.appliesProduct === entitlement.code &&
              !isEntitlementIncluded,
          ),
        )
        return {...entitlement, applicableOneTimes}
      })
    })

    formattedCentersAndApps.apps.forEach(app => {
      const isAppIncluded = includedApps?.some(code => code === app.code)
      const oneTimeFees = formattedCentersAndApps?.oneTimes.filter(
        oneTime => oneTime.appliesTo.length > 0,
      )
      const applicableOneTimes = oneTimeFees.filter(fee =>
        fee.appliesTo.some(
          rule => rule.appliesProduct === app.code && !isAppIncluded,
        ),
      )
      const uniqueOneTimes = new Set(
        formattedCentersAndApps.oneTimes.concat(applicableOneTimes),
      )
      formattedCentersAndApps.oneTimes = Array.from(uniqueOneTimes)

      app.entitlements = (app.entitlements ?? []).map(entitlement => {
        const isEntitlementIncluded = includedEntitlements?.some(
          ({code}) => code === entitlement.code,
        )
        const entApplicableOneTimes = oneTimeFees.filter(fee =>
          fee.appliesTo.some(
            rule =>
              rule.appliesProduct === entitlement.code &&
              !isEntitlementIncluded,
          ),
        )
        return {...entitlement, applicableOneTimes: entApplicableOneTimes}
      })
    })

    const isListingManagementExist = selectedCentersAndApps.apps.find(
      ({code}) => code === 'app-listings',
    )

    if (!isListingManagementExist) {
      formattedCentersAndApps.centers.map(center => {
        if (BC_PLANS.includes(center.code)) {
          center.entitlements = center.entitlements.filter(
            item => item.code !== 'app-listings-mgmt-free',
          )
        }
        return center
      })
    }
    setPlans(formattedCentersAndApps)
    // eslint-disable-next-line
  }, [
    all5PlanPrices,
    isUpgrade,
    accountInfo?.billingAddress?.country,
    selectedCountry,
    seatQty,
    showHipaa,
  ])

  // Recursive function to fetch nested dependent products
  function fetchDependentProducts(
    newlyAddedProductsArg,
    allProductsArg,
    removedProduct,
  ) {
    console.log(
      'Inside fetchDependentProducts, newlyAddedProductsArg :: ',
      newlyAddedProductsArg,
    )
    console.log(
      'Inside fetchDependentProducts, allProductsArg :: ',
      allProductsArg,
    )
    console.log(
      'Inside fetchDependentProducts, removedProduct :: ',
      removedProduct,
    )
    // Loop through all newly added products and remove the ones
    // that are dependent on the item being removed
    newlyAddedProductsArg.forEach(newProd => {
      console.log('Checking newProd :: ', JSON.stringify(newProd))
      let newProductRequirements
      const normalizedRequires =
        newProd?.configuration?.requires?.length > 0
          ? newProd.configuration.requires
          : newProd?.configuration?.requiresCenter || []
      if (normalizedRequires && normalizedRequires.length > 0)
        newProductRequirements = [...normalizedRequires]
      if (
        newProd?.configuration?.requiresAddon &&
        newProd?.configuration?.requiresAddon.length > 0
      )
        newProductRequirements = [...newProd.configuration.requiresAddon]
      if (
        newProd?.configuration?.appliesTo &&
        newProd?.configuration?.appliesTo.length > 0
      )
        newProductRequirements = newProd.configuration.appliesTo.map(
          r => r.appliesProduct,
        )
      // Add the product to productsToRemove array when
      // it does not have any product mentioned as pre-requisite
      // other than the item being removed in allProducts array
      if (newProductRequirements && newProductRequirements.length > 0) {
        if (
          !newProductRequirements.find(prod => {
            return (
              prod !== removedProduct.code &&
              allProductsArg.map(item => item.code).includes(prod)
            )
          })
        ) {
          console.log('Adding newProd as dependent product')
          allProductsArg = allProductsArg.filter(
            item => item.code !== newProd.code,
          )
          dependentProds.push(newProd)
          fetchDependentProducts(
            newlyAddedProductsArg.filter(item => item.code !== newProd.code),
            allProductsArg.filter(item => item.code !== newProd.code),
            newProd,
          )
        }
      }
    })
  }

  let dependentProds

  const handleRemoveItem = (removedItemCode, removedItemType) => {
    try {
      console.log('From catalog.js, removed item is :: ', removedItemCode)
      // When any MC 2.0 plan is removed, reset the website downgrade modal so
      // it re-triggers if the user re-selects a downgrade plan.
      if (
        MC_2_0_PLANS.includes(removedItemCode) ||
        isTgpFoundationFamilyCode(removedItemCode)
      ) {
        setMc20WebsiteDowngradeModalState(RETAIN_WEBSITE_MODAL_STATES.CLOSE)
        setMc20WebsiteDowngradePlanName('')
      }
      dependentProds = []
      let productsToRemove = []
      let modifiedCenters
      let modifiedApps
      let modifiedOneTimes

      let removedProduct = {code: removedItemCode, prodType: removedItemType}
      const isSEOAlreadyIncluded =
        defaultCentersAndApps?.current?.centers?.some(
          ({code}) => code === 'seobase-5-thryv-m2m',
        ) ||
        defaultCentersAndApps?.current?.apps?.some(
          ({code}) => code === 'seobase-5-thryv-m2m',
        )

      // All products -- recently added + already existing on the account
      let allProducts = [
        ...selectedCentersAndApps.centers
          .filter(item => item.code !== removedItemCode)
          .map(item => {
            return {
              code: item.code,
              prodType: 'Center',
            }
          }),
        ...selectedCentersAndApps.apps
          .filter(item => item.code !== removedItemCode)
          .map(item => {
            return {
              code: item.code,
              prodType: 'App',
            }
          }),
        ...selectedCentersAndApps.oneTimes
          .filter(item => item.code !== removedItemCode)
          .map(item => {
            return {
              code: item.code,
              prodType: 'oneTimeFee',
            }
          }),
      ]
      // Added newly to the order
      let newlyAddedProducts = [
        ...selectedCentersAndApps.centers
          .filter(
            item => !item.alreadyIncluded && item.code !== removedItemCode,
          )
          .map(item => {
            return {
              code: item.code,
              prodType: 'Center',
              configuration: all5PlanPrices.centers.find(
                center => center.code === item.code,
              ),
            }
          }),
        ...selectedCentersAndApps.apps
          .filter(
            item => !item.alreadyIncluded && item.code !== removedItemCode,
          )
          .map(item => {
            return {
              code: item.code,
              prodType: 'App',
              configuration: all5PlanPrices.apps.find(
                app => app.code === item.code,
              ),
            }
          }),
        ...selectedCentersAndApps.oneTimes
          .filter(
            item => !item.alreadyIncluded && item.code !== removedItemCode,
          )
          .map(item => {
            return {
              code: item.code,
              prodType: 'oneTimeFee',
              configuration: all5PlanPrices.oneTimeFees.find(
                oneTimeProd => oneTimeProd.code === item.code,
              ),
            }
          }),
      ]

      //check if default listing refresh is there in apps
      let defaultListingRefresh = defaultCentersAndApps.current.apps.find(
        item => item.code === 'addon-5-listrefrsh-m2m',
      )

      // Upgrade/Downgrade logic for Marketing center, Business center and Growth packages //Quantity reset logic for variable priced items
      if (
        removedProduct.code === 'mc_pro-5-thryv-m2m' &&
        defaultCentersAndApps.current.centers.find(
          item => item.code === 'mc_plus-5-thryv-m2m',
        )
      ) {
        let mcPlus = defaultCentersAndApps.current.centers.find(
          item => item.code === 'mc_plus-5-thryv-m2m',
        )
        modifiedCenters = selectedCentersAndApps.centers.map(item =>
          item.code === removedProduct.code ? mcPlus : item,
        )
      } else if (
        removedProduct.code === 'bc_pro-5-thryv-m2m' &&
        defaultCentersAndApps.current.centers.find(
          item => item.code === 'bc_plus-5-thryv-m2m',
        )
      ) {
        let bcPlus = defaultCentersAndApps.current.centers.find(
          item => item.code === 'bc_plus-5-thryv-m2m',
        )
        modifiedCenters = selectedCentersAndApps.centers.map(item =>
          item.code === removedProduct.code ? bcPlus : item,
        )
      } else if (
        removedProduct.code === 'bc_unlm-5-thryv-m2m' &&
        defaultCentersAndApps.current.centers.find(
          item => item.code === 'bc_plus-5-thryv-m2m',
        )
      ) {
        let bcPlus = defaultCentersAndApps.current.centers.find(
          item => item.code === 'bc_plus-5-thryv-m2m',
        )
        modifiedCenters = selectedCentersAndApps.centers.map(item =>
          item.code === removedProduct.code ? bcPlus : item,
        )
      } else if (
        removedProduct.code === 'bc_unlm-5-thryv-m2m' &&
        defaultCentersAndApps.current.centers.find(
          item => item.code === 'bc_pro-5-thryv-m2m',
        )
      ) {
        let bcPro = defaultCentersAndApps.current.centers.find(
          item => item.code === 'bc_pro-5-thryv-m2m',
        )
        modifiedCenters = selectedCentersAndApps.centers.map(item =>
          item.code === removedProduct.code ? bcPro : item,
        )
      } else if (
        removedProduct.code === 'keap_kcp-cert-5-fee-annual' ||
        removedProduct.code === 'keap_kcc-cert-5-fee-annual'
      ) {
        let currentCenter = defaultCentersAndApps.current.centers.find(
          center => center.code === removedProduct.code,
        )
        if (currentCenter) {
          modifiedCenters = selectedCentersAndApps.centers.map(center =>
            center.code === removedProduct.code ? currentCenter : center,
          )
        }
      } else if (
        removedProduct.code === 'addon-5-mcexpangr' &&
        defaultCentersAndApps.current.apps.find(
          item => item.code === 'addon-5-mcstartgr',
        )
      ) {
        let starterGrowth = defaultCentersAndApps.current.apps.find(
          item => item.code === 'addon-5-mcstartgr',
        )
        modifiedApps = selectedCentersAndApps.apps.map(item =>
          item.code === removedProduct.code ? starterGrowth : item,
        )
      } else if (
        removedProduct.code === 'addon-socialadsbronze-5-m2m' ||
        removedProduct.code === 'addon-socialadssilver-5-m2m' ||
        removedProduct.code === 'addon-socialadsgold-5-m2m' ||
        removedProduct.code === 'addon-5-seopb-m2m' ||
        removedProduct.code === 'addon-5-seokey-m2m' ||
        removedProduct.code === 'addon-5-keap-addusers-m2m' ||
        removedProduct.code === 'addon-5-keap-addcontacts-m2m' ||
        removedProduct.code === 'addon-5-keap-textvoice-m2m' ||
        removedProduct.code === 'addon-5-keap-addusers-annual' ||
        removedProduct.code === 'addon-5-keap-addcontacts-annual' ||
        removedProduct.code === 'addon-5-keap-textvoice-annual' ||
        removedProduct.code === 'addon-5-ai-webbuilder' ||
        removedProduct.code === 'addon-ctn-bninat-m2m' ||
        removedProduct.code === 'addon-ctn-bninat-annual' ||
        // FULSAS-13425: clicking remove on an already-owned Thryv Geo
        // Expansion tier reverts it to its existing state instead of
        // dropping it from the order entirely.
        GEO_ADDON_CODES.includes(removedProduct.code)
      ) {
        let currentApp = defaultCentersAndApps.current.apps.find(
          item => item.code === removedProduct.code,
        )
        if (currentApp) {
          modifiedApps = selectedCentersAndApps.apps.map(item =>
            item.code === removedProduct.code ? currentApp : item,
          )
        } else {
          productsToRemove.push(removedProduct)
        }
      } else if (removedProduct.code === 'seobase-5-thryv-m2m') {
        let currentDefault =
          defaultCentersAndApps.current.centers.find(
            item => item.code === removedProduct.code,
          ) ||
          defaultCentersAndApps.current.apps?.find(
            item => item.code === removedProduct.code,
          )
        if (currentDefault) {
          const isInCenters = selectedCentersAndApps.centers.some(
            item => item.code === removedProduct.code,
          )
          const isInApps = selectedCentersAndApps.apps.some(
            item => item.code === removedProduct.code,
          )
          if (isInCenters) {
            modifiedCenters = selectedCentersAndApps.centers.map(item =>
              item.code === removedProduct.code ? currentDefault : item,
            )
          } else if (isInApps) {
            modifiedApps = selectedCentersAndApps.apps.map(item =>
              item.code === removedProduct.code ? currentDefault : item,
            )
          }
          if (alphaSEOFromRecurly.alphaProposalId > 0) {
            setAlphaProposalId(alphaSEOFromRecurly.alphaProposalId)
            setAlphaDomain(alphaSEOFromRecurly.alphaDomain)
            setAlphaAmount(alphaSEOFromRecurly.alphaAmount)
            setIsExternalDomain(alphaSEOFromRecurly.alphaIsExternalDomain)
            setFutureBillingDate(
              alphaSEOFromRecurly.alphaStartDate || undefined,
            )
          }
        } else {
          productsToRemove.push(removedProduct)
        }
      } else if (
        removedProduct.code === 'addon-5-stratcontent-m2m' &&
        defaultListingRefresh
      ) {
        modifiedApps = selectedCentersAndApps.apps.map(item =>
          item.code === removedProduct.code ? defaultListingRefresh : item,
        )
      } else if (
        KEAP_FAMILY_M2M_CODES.includes(removedProduct.code) &&
        defaultCentersAndApps.current.centers.find(item =>
          KEAP_FAMILY_LEGACY_M2M_CODES.includes(item.code),
        )
      ) {
        let legacyKeapPlan = defaultCentersAndApps.current.centers.find(item =>
          KEAP_FAMILY_LEGACY_M2M_CODES.includes(item.code),
        )

        modifiedCenters = selectedCentersAndApps.centers.map(item =>
          item.code === removedProduct.code ? legacyKeapPlan : item,
        )
      } else if (
        KEAP_FAMILY_ANNUAL_CODES.includes(removedProduct.code) &&
        defaultCentersAndApps.current.centers.find(item =>
          KEAP_FAMILY_LEGACY_ANNUAL_CODES.includes(item.code),
        )
      ) {
        let legacyKeapPlanAnnual = defaultCentersAndApps.current.centers.find(
          item => KEAP_FAMILY_LEGACY_ANNUAL_CODES.includes(item.code),
        )

        modifiedCenters = selectedCentersAndApps.centers.map(item =>
          item.code === removedProduct.code ? legacyKeapPlanAnnual : item,
        )
      } else if (
        MC_2_0_PLANS.includes(removedProduct.code) &&
        defaultCentersAndApps.current.centers.find(item =>
          MC_2_0_PLANS.includes(item.code),
        )
      ) {
        let previousMC20Plan = defaultCentersAndApps.current.centers.find(
          item => MC_2_0_PLANS.includes(item.code),
        )
        modifiedCenters = selectedCentersAndApps.centers.map(item =>
          item.code === removedProduct.code ? previousMC20Plan : item,
        )
        const restoredPlanHasWebsite = (
          previousMC20Plan?.entitlements ?? []
        ).some(e => e.code === 'ent-5-website')
        if (restoredPlanHasWebsite) {
          modifiedApps = selectedCentersAndApps.apps.filter(
            item => item.code !== 'addon-5-website_difm',
          )
        }
      } else if (
        isTgpFoundationFamilyCode(removedProduct.code) &&
        defaultCentersAndApps.current.centers.find(item =>
          MC_2_0_PLANS.includes(item.code),
        )
      ) {
        let previousMC20Plan = defaultCentersAndApps.current.centers.find(
          item => MC_2_0_PLANS.includes(item.code),
        )
        modifiedCenters = selectedCentersAndApps.centers.map(item =>
          item.code === removedProduct.code ? previousMC20Plan : item,
        )
        // Signature/Premier already include ent-5-website, so the standalone
        // website addon added via the downgrade modal is no longer needed.
        // Remove it here so the fee reconciliation below doesn't re-add thryvdesg2.
        const restoredPlanHasWebsite = (
          previousMC20Plan?.entitlements ?? []
        ).some(e => e.code === 'ent-5-website')
        if (restoredPlanHasWebsite) {
          modifiedApps = selectedCentersAndApps.apps.filter(
            item => item.code !== 'addon-5-website_difm',
          )
        }
      } else {
        productsToRemove.push(removedProduct)
      }

      // SEO phase 2 changes to handle FSD on remove website
      if (WEBSITE_5_CODES.includes(removedProduct.code)) {
        const stripSeoFSD = item => {
          if (item.code === 'seobase-5-thryv-m2m' && !isSEOAlreadyIncluded) {
            if (item?.startDate) {
              const {startDate, ...rest} = item
              console.log('startDate', startDate)
              toast.warn(
                'Thryv Website must be included on the order to enable Future Start Date',
              )
              return rest
            } else {
              return item
            }
          }
          return item
        }
        modifiedCenters = selectedCentersAndApps.centers.map(stripSeoFSD)
        modifiedApps = selectedCentersAndApps.apps.map(stripSeoFSD)
        console.log('futureBillingDate', futureBillingDate)
        if (alphaSEOFromRecurly.alphaProposalId > 0) {
          setFutureBillingDate(alphaSEOFromRecurly.alphaStartDate || undefined)
        } else {
          setFutureBillingDate(undefined)
        }
      }

      // HVAC product scenarios
      if (
        removedProduct.code === 'mc_pro-5-thryv-m2m' ||
        removedProduct.code === 'mc_plus-5-thryv-m2m'
      ) {
        if (
          newlyAddedProducts.find(item => item.code === 'seobase-5-thryv-m2m')
        ) {
          productsToRemove.push({
            code: 'seobase-5-thryv-m2m',
            prodType: 'App',
          })
        }
        if (
          newlyAddedProducts.find(item => item.code === 'addon-5-seokey-m2m')
        ) {
          productsToRemove.push({
            code: 'addon-5-seokey-m2m',
            prodType: 'App',
          })
        }
        if (
          newlyAddedProducts.find(item => item.code === 'addon-5-seopb-m2m')
        ) {
          productsToRemove.push({
            code: 'addon-5-seopb-m2m',
            prodType: 'App',
          })
        }
        if (
          newlyAddedProducts.find(item => item.code === 'tl-mc-5-thryv-m2m')
        ) {
          productsToRemove.push({code: 'tl-mc-5-thryv-m2m', prodType: 'App'})
        }
        if (
          removedProduct.code === 'mc_pro-5-thryv-m2m' &&
          selectedCentersAndApps.centers
            .find(item => item.code === 'mc_pro-5-thryv-m2m')
            ?.entitlements?.find(addon => addon.code === 'ent-5-website')
        ) {
          if (
            newlyAddedProducts.find(item =>
              [
                'maxhvac-5-thryv-m2m',
                'maxroof-5-thryv-m2m',
                'maxelect-5-thryv-m2m',
                'maxplumb-5-thryv-m2m',
              ].includes(item.code),
            ) &&
            newlyAddedProducts.find(
              item =>
                item.code === 'vert_mc_web_clientsvcs-5-thryv-m2m' ||
                item.code === 'vert_mc_web_clientsvcs-5-thryv-ot',
            )
          ) {
            // Replace (vertical MC with website) with vertical
            let verticalProductAdded = newlyAddedProducts.find(
              item =>
                item.code === 'vert_mc_web_clientsvcs-5-thryv-m2m' ||
                item.code === 'vert_mc_web_clientsvcs-5-thryv-ot',
            )
            let verticalProductReplacement
            if (
              verticalProductAdded.code === 'vert_mc_web_clientsvcs-5-thryv-m2m'
            ) {
              verticalProductReplacement = formatItemOnRemoval(
                all5PlanPrices.apps.find(
                  item => item.code === 'vert_clientsvcs-5-thryv-m2m',
                ),
                selectedCountry,
                keapMaxIcon,
                'Keap Add-Ons',
              )
              modifiedApps = selectedCentersAndApps.apps.map(item =>
                item.code === verticalProductAdded.code
                  ? verticalProductReplacement
                  : item,
              )
            } else {
              verticalProductReplacement = formatItemOnRemoval(
                all5PlanPrices.oneTimeFees.find(
                  item => item.code === 'vert_clientsvcs-5-thryv-ot',
                ),
                selectedCountry,
                keapMaxIcon,
                'Keap Add-Ons',
              )
              modifiedOneTimes = selectedCentersAndApps.oneTimes.map(item =>
                item.code === verticalProductAdded.code
                  ? verticalProductReplacement
                  : item,
              )
            }
          }
        } else {
          if (
            newlyAddedProducts.find(item =>
              [
                'maxhvac-5-thryv-m2m',
                'maxroof-5-thryv-m2m',
                'maxelect-5-thryv-m2m',
                'maxplumb-5-thryv-m2m',
              ].includes(item.code),
            ) &&
            newlyAddedProducts.find(
              item =>
                item.code === 'vert_mc_clientsvcs-5-thryv-m2m' ||
                item.code === 'vert_mc_clientsvcs-5-thryv-ot',
            )
          ) {
            // Replace (vertical MC) with vertical
            let verticalProductAdded = newlyAddedProducts.find(
              item =>
                item.code === 'vert_mc_clientsvcs-5-thryv-m2m' ||
                item.code === 'vert_mc_clientsvcs-5-thryv-ot',
            )
            let verticalProductReplacement
            if (
              verticalProductAdded.code === 'vert_mc_clientsvcs-5-thryv-m2m'
            ) {
              verticalProductReplacement = formatItemOnRemoval(
                all5PlanPrices.apps.find(
                  item => item.code === 'vert_clientsvcs-5-thryv-m2m',
                ),
                selectedCountry,
                keapMaxIcon,
                'Keap Add-Ons',
              )
              modifiedApps = selectedCentersAndApps.apps.map(item =>
                item.code === verticalProductAdded.code
                  ? verticalProductReplacement
                  : item,
              )
            } else {
              verticalProductReplacement = formatItemOnRemoval(
                all5PlanPrices.oneTimeFees.find(
                  item => item.code === 'vert_clientsvcs-5-thryv-ot',
                ),
                selectedCountry,
                keapMaxIcon,
                'Keap Add-Ons',
              )
              modifiedOneTimes = selectedCentersAndApps.oneTimes.map(item =>
                item.code === verticalProductAdded.code
                  ? verticalProductReplacement
                  : item,
              )
            }
          }
        }
      }

      if (
        [
          'maxhvac-5-thryv-m2m',
          'maxroof-5-thryv-m2m',
          'maxelect-5-thryv-m2m',
          'maxplumb-5-thryv-m2m',
        ].includes(removedProduct.code) &&
        !newlyAddedProducts.find(
          item =>
            item.code !== removedProduct.code &&
            [
              'maxhvac-5-thryv-m2m',
              'maxroof-5-thryv-m2m',
              'maxelect-5-thryv-m2m',
              'maxplumb-5-thryv-m2m',
            ].includes(item.code),
        )
      ) {
        if (
          newlyAddedProducts.find(item =>
            [
              'vert_clientsvcs-5-thryv-ot',
              'vert_clientsvcs-5-thryv-m2m',
              'vert_mc_clientsvcs-5-thryv-m2m',
              'vert_mc_clientsvcs-5-thryv-ot',
              'vert_mc_web_clientsvcs-5-thryv-m2m',
              'vert_mc_web_clientsvcs-5-thryv-ot',
            ].includes(item.code),
          )
        ) {
          productsToRemove = [
            ...productsToRemove,
            ...newlyAddedProducts.filter(item =>
              [
                'vert_clientsvcs-5-thryv-ot',
                'vert_clientsvcs-5-thryv-m2m',
                'vert_mc_clientsvcs-5-thryv-m2m',
                'vert_mc_clientsvcs-5-thryv-ot',
                'vert_mc_web_clientsvcs-5-thryv-m2m',
                'vert_mc_web_clientsvcs-5-thryv-ot',
              ].includes(item.code),
            ),
          ]
        }
      }

      if (
        KEAP_FAMILY_CODES.includes(removedProduct.code) &&
        newlyAddedProducts.find(item =>
          [
            'vert_clientsvcs-5-thryv-ot',
            'vert_clientsvcs-5-thryv-m2m',
            'vert_mc_clientsvcs-5-thryv-m2m',
            'vert_mc_clientsvcs-5-thryv-ot',
            'vert_mc_web_clientsvcs-5-thryv-m2m',
            'vert_mc_web_clientsvcs-5-thryv-ot',
          ].includes(item.code),
        )
      ) {
        productsToRemove = [
          ...productsToRemove,
          ...newlyAddedProducts.filter(item =>
            [
              'vert_clientsvcs-5-thryv-ot',
              'vert_clientsvcs-5-thryv-m2m',
              'vert_mc_clientsvcs-5-thryv-m2m',
              'vert_mc_clientsvcs-5-thryv-ot',
              'vert_mc_web_clientsvcs-5-thryv-m2m',
              'vert_mc_web_clientsvcs-5-thryv-ot',
            ].includes(item.code),
          ),
        ]
      }
      console.log('productsToRemove :: ', productsToRemove)
      console.log('newlyAddedProducts :: ', newlyAddedProducts)
      console.log('allProducts :: ', allProducts)
      fetchDependentProducts(newlyAddedProducts, allProducts, removedProduct)
      productsToRemove = [...productsToRemove, ...dependentProds]
      // Remove the newly auto added centers, if the parent center product is being removed
      if (removedProduct.prodType === 'Center') {
        let removedProdConfiguration = all5PlanPrices.centers.find(
          center => center.code === removedProduct.code,
        )
        if (
          removedProdConfiguration?.autoAddCenters &&
          removedProdConfiguration?.autoAddCenters?.length > 0
        ) {
          removedProdConfiguration?.autoAddCenters.forEach(autoCenter => {
            productsToRemove.push({code: autoCenter.code, prodType: 'Center'})
          })
        }

        // When MC is removed and BC 6 becomes standalone, remove Listing Refresh
        const MC_CENTER_CODES = [
          'mc_plus-5-thryv-m2m',
          'mc_pro-5-thryv-m2m',
          'mc_pro_enh-5-thryv-m2m',
          'mc_foundation-5-thryv-m2m',
          ...THRYV_BASIC_CODES,
          ...MC_TRIAL_PLAN_CODES,
        ]
        const BC_6_CODE = 'bc-6-thryv-m2m'
        const OTHER_BC_CODES = [
          'bc_plus-5-thryv-m2m',
          'bc_pro-5-thryv-m2m',
          'bc_unlm-5-thryv-m2m',
          'bc_ent-6-thryv-m2m',
          'bc_ent-unlm-5-thryv-m2m',
        ]
        const LISTING_REFRESH_CODES = [
          'addon-4-listrefrsh-m2m',
          'addon-5-listrefrsh-m2m',
        ]

        const isRemovedProductMC = MC_CENTER_CODES.includes(removedProduct.code)
        const remainingCenters = selectedCentersAndApps.centers.filter(
          c => c.code !== removedProduct.code,
        )
        const isBc6Standalone =
          isRemovedProductMC &&
          remainingCenters.some(({code}) => code === BC_6_CODE) &&
          !remainingCenters.some(({code}) => MC_CENTER_CODES.includes(code)) &&
          !remainingCenters.some(({code}) => OTHER_BC_CODES.includes(code))

        if (isBc6Standalone) {
          LISTING_REFRESH_CODES.forEach(code => {
            productsToRemove.push({code, prodType: 'App'})
          })
        }
      }
      console.log('productsToRemove with dependents:: ', productsToRemove)
      console.log('productsToRemove with autoAddCenters:: ', productsToRemove)
      // Reset the order related configurations
      productsToRemove.forEach(itemToRemove => {
        switch (itemToRemove.code) {
          case 'mc_plus-5-thryv-m2m':
          case 'mc_pro-5-thryv-m2m': {
            setMcDomainConfig(DEFAULT_MC_CONFIG)
            break
          }
          case 'tl-mc-5-thryv-m2m':
          case 'tl-tax-5-thryv-m2m':
          case 'tl-5-thryv-m2m': {
            setThryvLeadsId('')
            setThryvLeadsValidation(null)
            setTlDomainConfig(DEFAULT_MC_CONFIG)
            break
          }
          case 'addon-5-leadslift-m2m':
          case 'addon-5-leadslift-bni-m2m': {
            setLlDomainConfig(DEFAULT_MC_CONFIG)
            break
          }
          case 'addon-5-website_difm': {
            setSensisSiteDomain('')
            break
          }
          case 'ent-5-website': {
            setRetainWebsiteModalState(RETAIN_WEBSITE_MODAL_STATES.CLOSE)
            setMc20WebsiteDowngradeModalState(RETAIN_WEBSITE_MODAL_STATES.CLOSE)
            setMc20WebsiteDowngradePlanName('')
            break
          }
          case 'seo-5-thryv-m2m': {
            setProposalId('')
            setSeoDomainConfig(DEFAULT_SEO_CONFIG)
            setProposalValidation(null)
            setFutureBillingDate(undefined)
            setIsSEOMBGDisabled(false)
            break
          }
          case 'seobase-5-thryv-m2m': {
            if (alphaSEOFromRecurly.alphaProposalId > 0) {
              setAlphaProposalId(alphaSEOFromRecurly.alphaProposalId)
              setAlphaDomain(alphaSEOFromRecurly.alphaDomain)
              setAlphaAmount(alphaSEOFromRecurly.alphaAmount)
              setIsExternalDomain(alphaSEOFromRecurly.alphaIsExternalDomain)
              setFutureBillingDate(
                alphaSEOFromRecurly.alphaStartDate || undefined,
              )
            } else {
              setAlphaProposalId('')
              setAlphaDomain('')
              setIsExternalDomain(null)
              setAlphaAmount(null)
              setFutureBillingDate(undefined)
            }
            break
          }
          case 'addon-5-seokey-m2m': {
            if (alphaSEOFromRecurly.alphaProposalId > 0)
              setAlphaKeyCount(alphaSEOFromRecurly.alphaKeyCount)
            else setAlphaKeyCount(null)
            break
          }

          case 'addon-5-seopb-m2m': {
            if (alphaSEOFromRecurly.alphaProposalId > 0)
              setAlphaPowerboostCount(alphaSEOFromRecurly.alphaPowerboostCount)
            else setAlphaPowerboostCount(null)
            break
          }
          case 'addon-5-keap-addcontacts-m2m': {
            // Keep Add Contacts unchanged unless user explicitly reselects it.
            setKeapAddContactsQty('')
            break
          }
          case 'addon-5-keap-addcontacts-annual': {
            if (
              keapAddContactsRecurlyQtyAnnual &&
              keapAddContactsRecurlyQtyAnnual > 0
            ) {
              setKeapAddContactsQtyAnnual(keapAddContactsRecurlyQtyAnnual)
            } else {
              setKeapAddContactsQtyAnnual('')
            }
            break
          }
          case 'addon-5-keap-legacy-graphly-contacts-annual': {
            if (
              keapAddContactsRecurlyQtyGraphlyAnnual &&
              keapAddContactsRecurlyQtyGraphlyAnnual > 0
            ) {
              setKeapAddContactsQtyGraphlyAnnual(
                keapAddContactsRecurlyQtyGraphlyAnnual,
              )
            } else {
              setKeapAddContactsQtyGraphlyAnnual('')
            }
            break
          }
          case 'addon-5-keap-legacy-graphly-contacts-m2m': {
            if (
              keapAddContactsRecurlyQtyGraphlyM2m &&
              keapAddContactsRecurlyQtyGraphlyM2m > 0
            ) {
              setKeapAddContactsQtyGraphlyM2m(
                keapAddContactsRecurlyQtyGraphlyM2m,
              )
            } else {
              setKeapAddContactsQtyGraphlyM2m('')
            }
            break
          }
          case 'addon-5-keap-legacy-plusthis-premium-trans-m2m': {
            if (
              plusThisPremiumRecurlyQtyM2m &&
              plusThisPremiumRecurlyQtyM2m > 0
            ) {
              setPlusThisPremiumQtyGraphlyM2m(plusThisPremiumRecurlyQtyM2m)
            } else {
              setPlusThisPremiumQtyGraphlyM2m('')
            }
            break
          }
          case 'addon-5-keap-textvoice-m2m': {
            if (keapTextVoiceRecurlyQty && keapTextVoiceRecurlyQty > 0) {
              setKeapTextVoiceQty(keapTextVoiceRecurlyQty)
            } else {
              setKeapTextVoiceQty(1)
            }
            break
          }
          case 'addon-5-keap-textvoice-annual': {
            if (
              keapTextVoiceRecurlyQtyAnnual &&
              keapTextVoiceRecurlyQtyAnnual > 0
            ) {
              setKeapTextVoiceQtyAnnual(keapTextVoiceRecurlyQtyAnnual)
            } else {
              setKeapTextVoiceQtyAnnual(1)
            }
            break
          }
          case 'keap_kcp-cert-5-fee-annual': {
            if (kcpRecurlyOption && kcpRecurlyOption > 0) {
              let matchedOption = KCP_OPTIONS.filter(
                option => option.value === kcpRecurlyOption,
              )
              setSelectedKcpOption(matchedOption[0])
            } else {
              setSelectedKcpOption('')
            }
          }
          case 'keap_max-5-thryv-m2m':
          case 'keap_pro-5-thryv-m2m':
          case 'keap_ultimate-5-thryv-m2m':
          case 'keap_ultimate-5-thryv-part-m2m':
          case 'keap_max-5-thryv-part-m2m': {
            setIsFreeTrailCustomer(null)
            setKeapDomainConfig(DEFAULT_KEAP_CONFIG)
            setKeapTrailAppId(null)
            break
          }
          case 'hvac-5-thryv-m2m': {
            setHvacDomainConfig(DEFAULT_HVAC_CONFIG)
            break
          }
          default:
            break
        }
      })

      if (modifiedCenters && modifiedCenters.length > 0) {
        modifiedCenters = modifiedCenters.filter(item => {
          if (productsToRemove.map(prod => prod.code).includes(item.code))
            return false
          else return true
        })
      } else {
        modifiedCenters = selectedCentersAndApps.centers.filter(item => {
          if (productsToRemove.map(prod => prod.code).includes(item.code))
            return false
          else return true
        })
      }

      if (modifiedApps && modifiedApps.length > 0) {
        modifiedApps = modifiedApps.filter(item => {
          if (productsToRemove.map(prod => prod.code).includes(item.code))
            return false
          else return true
        })
      } else {
        modifiedApps = selectedCentersAndApps.apps.filter(item => {
          if (productsToRemove.map(prod => prod.code).includes(item.code))
            return false
          else return true
        })
      }

      if (modifiedOneTimes && modifiedOneTimes.length > 0) {
        modifiedOneTimes = modifiedOneTimes.filter(item => {
          if (productsToRemove.map(prod => prod.code).includes(item.code))
            return false
          else return true
        })
      } else {
        modifiedOneTimes = selectedCentersAndApps.oneTimes.filter(item => {
          if (productsToRemove.map(prod => prod.code).includes(item.code))
            return false
          else return true
        })
      }

      // Restore products that were deactivated by the removed product
      // if they were part of the original account subscription
      const removedProdConfig = all5PlanPrices.centers.find(
        c => c.code === removedProduct.code,
      )
      const deactivatedCenterCodes = removedProdConfig?.disableCenters || []
      const deactivatedAppCodes = removedProdConfig?.disableApps || []

      deactivatedCenterCodes.forEach(code => {
        const originalCenter = defaultCentersAndApps.current?.centers?.find(
          c => c.code === code,
        )
        if (originalCenter && !modifiedCenters.some(c => c.code === code)) {
          modifiedCenters = [...modifiedCenters, originalCenter]
        }
      })

      deactivatedAppCodes.forEach(code => {
        const originalApp = defaultCentersAndApps.current?.apps?.find(
          a => a.code === code,
        )
        if (originalApp && !modifiedApps.some(a => a.code === code)) {
          modifiedApps = [...modifiedApps, originalApp]
        }
      })

      // Fee reconciliation: after removing a plan, recalculate which fees apply
      const oneTimeFeesCatalog = (plans?.oneTimes || []).filter(
        ot => ot.appliesTo?.length > 0,
      )
      if (oneTimeFeesCatalog.length > 0) {
        const finalCenters =
          modifiedCenters !== undefined
            ? modifiedCenters
            : selectedCentersAndApps.centers
        const finalApps =
          modifiedApps !== undefined
            ? modifiedApps
            : selectedCentersAndApps.apps
        const finalOneTimes =
          modifiedOneTimes !== undefined
            ? modifiedOneTimes
            : selectedCentersAndApps.oneTimes

        const existingCodesForFee = [
          ...(defaultCentersAndApps.current?.centers || []).map(c => c.code),
          ...(defaultCentersAndApps.current?.apps || []).map(a => a.code),
        ]
        const cartCodesForFee = [
          ...finalCenters.filter(c => !c.alreadyIncluded).map(c => c.code),
          ...finalApps.filter(a => !a.alreadyIncluded).map(a => a.code),
        ].filter((v, i, arr) => arr.indexOf(v) === i)

        const applicableFeeCodes = resolveApplicableFees(
          existingCodesForFee,
          cartCodesForFee,
          oneTimeFeesCatalog,
        )

        // Remove managed fees no longer applicable; add ones that should now apply
        const managedCodes = new Set(oneTimeFeesCatalog.map(f => f.code))
        const retained = finalOneTimes.filter(
          fee =>
            !managedCodes.has(fee.code) ||
            applicableFeeCodes.includes(fee.code),
        )
        const feesToAdd = oneTimeFeesCatalog
          .filter(fee => applicableFeeCodes.includes(fee.code))
          .filter(fee => !retained.some(o => o.code === fee.code))

        modifiedOneTimes = [...retained, ...feesToAdd]
      }

      setSelectedCentersAndApps(prevItems => ({
        ...prevItems,
        centers: modifiedCenters,
        apps: modifiedApps,
        oneTimes: modifiedOneTimes,
      }))

      // Common state variables that need to be reset
      setCode('')
      setSelectedPlan(null)
      setSelectedOptions([])
      // Removing all coupons from the order
      if (ccCouponInfo && ccCouponInfo.length > 0) {
        toast.warning(
          'Oops! Your cart changed and some coupons bailed. Re-add them to reel those savings back in',
        )
      }
      couponDispatch(resetAllCouponState())
      // Reset the special flags -- need to check the functionality
      setIsLegacyUpgradeAllowed(false)
      setCohortEaid('')
      setIsEaidFound(false)
      setAllowInvoice(false)
      // FULSAS-8726: only the EAID-linked cohort state is cleared above. The
      // account-level cohort flag (allowIncentive) must persist across cart
      // edits, otherwise removing an auto-added item (e.g. Listing Refresh)
      // permanently drops isCohort to false -- nothing re-asserts it -- which
      // re-applies the New Customer Incentive and drops the cohort OB-fee waiver.
      setIsCohort(!!accountSubscriptions?.accountInfo?.allowIncentive)
      // Reset the previewPayment flags
      if (setPreviewRequestFinished) setPreviewRequestFinished(false)
      // Removing the coupons from order tables realted state variable
      setOrder5LinesDiscounts?.([])
    } catch (e) {
      console.log('Exception is ::', e)
    }
  }

  const handleStartOver = () => {
    console.log('Inside handle start over')
    setRetainWebsiteModalState(RETAIN_WEBSITE_MODAL_STATES.CLOSE)
    setMc20WebsiteDowngradeModalState(RETAIN_WEBSITE_MODAL_STATES.CLOSE)
    setMc20WebsiteDowngradePlanName('')
    setSelectedCentersAndApps(defaultCentersAndApps.current)
    setSelectedPlan(null)
    setSelectedOptions([])
    setProposalId('')
    setProposalValidation(null)
    if (alphaSEOFromRecurly.alphaProposalId > 0) {
      setAlphaProposalId(alphaSEOFromRecurly.alphaProposalId)
      setAlphaDomain(alphaSEOFromRecurly.alphaDomain)
      setAlphaAmount(alphaSEOFromRecurly.alphaAmount)
      // setCategories(alphaSEOFromRecurly.alphaCategories)
      // setGeos(alphaSEOFromRecurly.alphaGeos)
      setAlphaKeyCount(alphaSEOFromRecurly.alphaKeyCount)
      setAlphaPowerboostCount(alphaSEOFromRecurly.alphaPowerboostCount)
      setIsExternalDomain(alphaSEOFromRecurly.alphaIsExternalDomain)
      setFutureBillingDate(alphaSEOFromRecurly.alphaStartDate || undefined)
    } else {
      setAlphaProposalId('')
      setAlphaDomain('')
      setIsExternalDomain(null)
      setAlphaAmount(null)
      setAlphaKeyCount(null)
      setAlphaPowerboostCount(null)
      setFutureBillingDate(undefined)
    }

    // Keep Add Contacts unchanged unless user explicitly reselects it.
    setKeapAddContactsQty('')

    if (keapTextVoiceRecurlyQty && keapTextVoiceRecurlyQty > 0) {
      setKeapTextVoiceQty(keapTextVoiceRecurlyQty)
    } else {
      setKeapTextVoiceQty(1)
    }
    setIsFreeTrailCustomer(null)
    // if (keapAddUsersRecurlyQty && keapAddUsersRecurlyQty >0 ){
    //   setKeapAddUsersQty(keapAddUsersRecurlyQty)
    // }else {
    //   setKeapAddUsersQty(1)
    // }

    setMcDomainConfig(DEFAULT_MC_CONFIG)
    setTlDomainConfig(DEFAULT_MC_CONFIG)
    setLlDomainConfig(DEFAULT_MC_CONFIG)
    setSeoDomainConfig(DEFAULT_SEO_CONFIG)
    setKeapDomainConfig(DEFAULT_KEAP_CONFIG)
    setKeapTrailAppId(null)
    setHvacDomainConfig(DEFAULT_HVAC_CONFIG)
    setCode('')
    // setCcAutoCouponInfo(null)
    couponDispatch(resetAllCouponState())
    setThryvLeadsId('')
    setThryvLeadsValidation(null)
    if (!alphaSEOFromRecurly.alphaProposalId) {
      setFutureBillingDate(undefined)
    }
    setSensisSiteDomain('')
    setIsSEOMBGDisabled(false)
    setIsLegacyUpgradeAllowed(false)
    setCohortEaid('')
    setIsEaidFound(false)
    setAllowInvoice(false)
    // FULSAS-8726: preserve the account-level cohort flag (allowIncentive)
    // across Start Over; only the EAID-linked cohort state is cleared above.
    setIsCohort(!!accountSubscriptions?.accountInfo?.allowIncentive)
    if (setPreviewRequestFinished) setPreviewRequestFinished(false)
    setOrder5LinesDiscounts?.([])
  }

  const calcTotal = items => {
    return items.reduce((acc, item) => {
      if (
        (item.name.includes('SEO') &&
          item.name !== 'AEO & SEO' &&
          item.name !== 'SEO Keywords' &&
          item.name !== 'SEO PowerBoost') ||
        item.name === 'Thryv Leads'
      ) {
        const price =
          item.code === 'addon-5-leadslift-m2m'
            ? item.unitAmount
            : item?.amount || 0
        return acc + parseFloat(price)
      }
      if (item.code === 'addon-5-leadslift-m2m') {
        const entries = Object.entries(getAmountToQuantityMap(selectedCountry))
        const closest = entries.reduce((prev, curr) =>
          Math.abs(curr[1] - item.quantity) < Math.abs(prev[1] - item.quantity)
            ? curr
            : prev,
        )
        const priceLL = Number(closest[0])
        return acc + priceLL
      }
      // addon-5-keap-addusers-m2m: amount is always unit price (CartItem/calcTotal convention)
      // Use unit price × quantity
      if (item.code === 'addon-5-keap-addusers-m2m') {
        const unitPrice =
          item.unitAmount ?? item.unit_amount ?? item?.amount ?? 0
        const quantity = item.quantity || 1
        return acc + Number(unitPrice) * Number(quantity)
      }
      const price =
        (item.code === 'addon-5-leadslift-m2m'
          ? item.unitAmount
          : item?.amount) || 0
      const quantity = item.quantity || 1
      return acc + price * quantity
    }, 0)
  }

  useEffect(() => {
    const calculateTotalPrice = () => {
      const centersTotalNo24x7SupportFee = calcTotal(
        selectedCentersAndApps.centers.filter(
          ({code}) => code !== '247support-m2m',
        ),
      )
      const isBCorMCInCart = selectedCentersAndApps?.centers.some(
        ({code}) => BC_PLANS.includes(code) || MC_PLANS.includes(code),
      )
      const isBCUnlimitedInOrder = selectedCentersAndApps?.centers?.some(
        ({code}) => code === 'bc_unlm-5-thryv-m2m',
      )
      console.log('inside catalog calc total price', {selectedCentersAndApps})
      const filteredApps = selectedCentersAndApps.apps.filter(
        ({code, name}) => {
          // Thryv Add-Ons parent is a container; real prices are in child addons (Add Users, Add Contacts, Text & Voice)
          if (code === 'addon-5-keap-addons') return false
          const isMCPEditor = code === 'addon-5-mktproeditor'
          if (isMCPEditor && isBCUnlimitedInOrder) {
            return false
          }
          const isListingsApp = code === 'app-listings'
          if (
            ['Starter Growth Package', 'Expanded Growth Package'].includes(name)
          ) {
            if (isNewTLMCAccessible) {
              let isTLLeadExist =
                selectedCentersAndApps.centers.find(
                  item => item.name === 'Thryv Leads',
                ) ||
                selectedCentersAndApps.apps?.find(
                  item => item.name === 'Thryv Leads',
                )
              if (isTLLeadExist) {
                return false
              }
            }
          }
          if (['seombg-5-thryv-m2m', 'seo-5-thryv-m2m'].includes(code)) {
            let isNewSEOExist =
              selectedCentersAndApps.centers.find(
                ({code}) => code === 'seobase-5-thryv-m2m',
              ) ||
              selectedCentersAndApps.apps?.find(
                ({code}) => code === 'seobase-5-thryv-m2m',
              )
            if (isNewSEOExist) {
              return false
            }
            return true
          }
          if (isListingsApp) return !isBCorMCInCart
          return true
        },
      )
      const centersTotal = calcTotal(selectedCentersAndApps.centers)
      const appsTotal = calcTotal(filteredApps)
      const oneTimesTotal = calcTotal(selectedCentersAndApps.oneTimes)
      const calculatedTotal =
        centersTotalNo24x7SupportFee + appsTotal + oneTimesTotal
      const monthlyTotal = centersTotal + appsTotal

      setMonthlyTotal(monthlyTotal)
      setTotalPrice(calculatedTotal)
    }
    calculateTotalPrice()
  }, [selectedCentersAndApps])

  useEffect(() => {
    if (
      accountSubscriptions &&
      accountSubscriptions?.accountInfo?.allowIncentive
    ) {
      setIsCohort(true)
    }
  }, [accountSubscriptions, setIsCohort])

  useEffect(() => {
    setSelectedCentersAndApps(currentCart => {
      const syncedCart = syncCoreKeapTextVoiceOverride(
        currentCart,
        defaultCentersAndApps.current,
      )

      return syncedCart === currentCart ? currentCart : syncedCart
    })
  }, [selectedCentersAndApps])

  useEffect(() => {
    if (!isUpgrade) return
    const planType = selectedPlan?.name.split(' ')[0]
    const isPlanTypeIncluded = selectedCentersAndApps.centers.some(({name}) =>
      name.includes(planType),
    )
    if (isPlanTypeIncluded) {
      const defaultOptions = selectedCentersAndApps.centers.find(({name}) =>
        name.includes(planType),
      ).entitlements
      setSelectedOptions(defaultOptions)
    } else {
      const isSignaturePlan = MC_2_0_SIGNATUREPLANS.includes(selectedPlan?.code)
      const websiteEnt = isSignaturePlan
        ? (selectedPlan?.entitlements ?? []).find(
            ({code}) => code === 'ent-5-website',
          )
        : null
      setSelectedOptions(websiteEnt ? [websiteEnt] : [])
    }
  }, [isUpgrade, selectedCentersAndApps.centers, selectedPlan?.name])

  useEffect(
    () => {
      const fetchNewOrderDefaultCenters = async () => {
        if (isNewOrder) {
          //check if non free items are existing
          const isAnyOtherAccountExisting =
            selectedCentersAndApps?.centers?.some(
              center => !FREE_ACCOUNT_CODES.includes(center.code),
            ) ||
            selectedCentersAndApps?.apps?.some(
              app => !FREE_ACCOUNT_CODES.includes(app.code),
            )
          if (!isAnyOtherAccountExisting) {
            // Pull from the provided order object
            const ai = order?.accountInformation || {}

            const billingAddress = {
              street1: ai.address || '',
              city: ai.city || '',
              region: ai.state || '',
              postalCode: ai.postCode || '',
              country: ai.country || selectedCountry?.isoValue || '',
              phone: ai.companyPhone || '',
            }

            const missingRequired =
              !billingAddress.street1 ||
              !billingAddress.city ||
              !billingAddress.region ||
              !billingAddress.postalCode

            if (missingRequired) {
              setShowUpdateAddressModal(true)
              setIsLoading(false)
              return
            }

            // Seed orderData5CU for the new-order flow (unchanged)
            const seededOrderData = {
              order,
              subscriptions: {childSubscriptions: [], parentSubscriptions: []},
              accountInfo: {
                company: ai.company || '',
                email: ai.email,
                firstName: ai.firstName,
                lastName: ai.lastName,
                recurlyEmail: ai.email,
                recurlyFirst: ai.firstName,
                recurlyLast: ai.lastName,
                recurlyCompany: ai.company,
                keapAccount: !!ai.keapAccount,
                billTo: ai.billTo,
                billingAddress,
                // new-order.js reads accountInfo.abn (AU ABN / NZ NZBN) to send it to
                // cc-order-service; without forwarding it here it never reaches Recurly.
                abn: ai.abn || '',
              },
              recurlyAccount: null,
            }
            setOrderData5CU(seededOrderData)
            // if (!isNewAccountOrderCreated) {
            //   captureOrder5NewUpgrade(
            //     'catalog-init-newAccOrder',
            //     'New',
            //     seededOrderData,
            //   )
            // }
            try {
              const config = prefetchedDefaultConfig
                ? prefetchedDefaultConfig
                : await client('plans/config/default')

              // Attach images using your getPlanIcon
              const withImgs = attachImgsToConfigPayload(config)
              // Set defaults from service
              defaultCentersAndApps.current = withImgs
            } catch (e) {
              console.log(
                'Falling back to hardcoded defaults, reason:',
                e?.message || e,
              )

              //Fallback: the old hardcoded object (kept as a safe default)
              defaultCentersAndApps.current = {
                centers: [
                  {
                    code: 'cc_basicpls-5-thryv-m2m',
                    name: 'My Account',
                    id: 'cc_basicpls-5-thryv-m2m',
                    img: getPlanIcon('My Account'),
                    groupTitle: 'Command Center',
                    entitlements: [],
                    amount: 0,
                    uuid: '',
                    unitAmount: 0,
                    quantity: 1,
                    addons: [],
                  },
                ],
                apps: [
                  {
                    id: 'xt2e88macu3g',
                    uuid: '',
                    state: 'active',
                    code: 'appmarket-5-thryv-m2m',
                    name: 'Thryv Add-Ons',
                    groupTitle: 'Thryv Add-Ons',
                    quantity: 1,
                    unitAmount: 0,
                    alphaDomain: null,
                    alphaProposalId: null,
                    img: getPlanIcon('Thryv Add-Ons'),
                    subtotal: 0,
                    total: 0,
                    addons: [
                      {
                        id: 'xt2e88qjwgey',
                        subscriptionId: '',
                        code: 'addon-5-signatures_usage',
                        name: 'Signatures Usage',
                        quantity: 1,
                        subtotal: null,
                        alreadyIncluded: true,
                        groupTitle: 'Thryv Add-Ons',
                        img: getPlanIcon('Signatures Usage'),
                        amount: 0,
                      },
                      {
                        id: 'xt2e88qlqqcs',
                        subscriptionId: '',
                        code: 'app-websitebuilder',
                        name: 'Website Builder',
                        img: getPlanIcon('Website Builder'),
                        quantity: 1,
                        unitAmount: 0,
                        subtotal: 0,
                        alreadyIncluded: true,
                        groupTitle: 'Thryv Add-Ons',
                        amount: 0,
                      },
                    ],
                    alreadyIncluded: true,
                    amount: 0,
                  },
                  {
                    id: 'xt2e88qjwgey',
                    subscriptionId: '',
                    code: 'addon-5-signatures_usage',
                    name: 'Signatures Usage',
                    img: getPlanIcon('Signatures Usage'),
                    quantity: 1,
                    subtotal: null,
                    alreadyIncluded: true,
                    groupTitle: 'Thryv Add-Ons',
                    amount: 0,
                  },
                  {
                    id: 'xt2e88qlqqcs',
                    subscriptionId: '',
                    code: 'app-websitebuilder',
                    name: 'Website Builder',
                    img: getPlanIcon('Website Builder'),
                    quantity: 1,
                    unitAmount: 0,
                    subtotal: 0,
                    alreadyIncluded: true,
                    groupTitle: 'Thryv Add-Ons',
                    amount: 0,
                  },
                ],
                oneTimes: [],
              }
            }

            expiredCentersAndApps.current = {
              centers: [],
              apps: [],
              oneTimes: [],
            }
            setSelectedCentersAndApps(
              prevCentersAndApps || defaultCentersAndApps.current,
            )
            setIsLoading(false)
            return
          }
        }
      }
      fetchNewOrderDefaultCenters()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isNewOrder],
  )

  useEffect(
    () => {
      if (hasKeapAccountAbility) {
        setKeapAccount(
          accountInfo?.keapAccount ||
            accountSubscriptionData?.accountInfo?.keapAccount ||
            false,
        )
      }
      if (isUpgrade) {
        const fetchAccountSubscriptions = async (retriesAmount = 9) => {
          try {
            let response
            if (prefetchedAccountSubscriptions) {
              response = prefetchedAccountSubscriptions
            } else {
              const url = `accounts/5/subscriptions?thryvId=${CCAccountInfo.thryvId}`
              response = await client(url)
            }
            if (
              !response.childSubscriptions.find(
                sub => sub.code === 'appmarket-5-thryv-m2m',
              ) ||
              !response.parentSubscriptions.find(sub =>
                sub.code.includes('cc_'),
              )
            ) {
              if (
                navFrom &&
                navFrom !== undefined &&
                navFrom !== null &&
                navFrom === 'accounts'
              ) {
                setShowErrorSubscriptionModal(true)
              } else {
                setShowErrorAccountModal(true)
              }

              //throw Error('missing subscription')
            }
            // check for Keap Additional Contacts && Keap Text & Voice
            try {
              if (!selectedCountry) {
                selectedCountry =
                  response?.accountInfo?.businessAddress?.country
              }
              let ADDITIONAL_CONTACT_OPTIONS =
                selectedCountry === 'US'
                  ? [
                      {name: 'Up To 1,000 Contacts: $36', value: '1000-36'},
                      {name: 'Up To 2,500 Contacts: $84', value: '2500-84'},
                      {name: 'Up To 5,000 Contacts: $150', value: '5000-150'},
                      {name: 'Up To 10,000 Contacts: $240', value: '10000-240'},
                      {name: 'Up To 15,000 Contacts: $330', value: '15000-330'},
                      {name: 'Up To 25,000 Contacts: $420', value: '25000-420'},
                      {name: 'Up To 50,000 Contacts: $480', value: '50000-480'},
                      {name: 'Up To 75,000 Contacts: $600', value: '75000-600'},
                      {
                        name: 'Up To 100,000 Contacts: $720',
                        value: '100000-720',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $1,200',
                        value: '200000-1200',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $1,980',
                        value: '500000-1980',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $2,940',
                        value: '750000-2940',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $3,720',
                        value: '1000000-3720',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $5,940',
                        value: '2000000-5940',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $7,200',
                        value: '3000000-7200',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $8,460',
                        value: '4000000-8460',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $10,140',
                        value: '5000000-10140',
                      },
                    ]
                  : selectedCountry === 'CA'
                  ? [
                      {name: 'Up To 1,000 Contacts: $49', value: '1000-49'},
                      {name: 'Up To 2,500 Contacts: $114', value: '2500-114'},
                      {name: 'Up To 5,000 Contacts: $203', value: '5000-203'},
                      {name: 'Up To 10,000 Contacts: $325', value: '10000-325'},
                      {name: 'Up To 15,000 Contacts: $446', value: '15000-446'},
                      {name: 'Up To 25,000 Contacts: $569', value: '25000-569'},
                      {name: 'Up To 50,000 Contacts: $649', value: '50000-649'},
                      {name: 'Up To 75,000 Contacts: $811', value: '75000-811'},
                      {
                        name: 'Up To 100,000 Contacts: $973',
                        value: '100000-973',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $1,622',
                        value: '200000-1622',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $2,676',
                        value: '500000-2676',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $3,973',
                        value: '750000-3973',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $5,027',
                        value: '1000000-5027',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $8,027',
                        value: '2000000-8027',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $9,730',
                        value: '3000000-9730',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $11,433',
                        value: '4000000-11433',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $13,703',
                        value: '5000000-13703',
                      },
                    ]
                  : selectedCountry === 'AU'
                  ? [
                      {
                        name: 'Up To 1,000 Contacts: AU $42.00',
                        value: '1000-42.00',
                      },
                      {
                        name: 'Up To 2,500 Contacts: AU $98.00',
                        value: '2500-98.00',
                      },
                      {
                        name: 'Up To 5,000 Contacts: AU $178.00',
                        value: '5000-178.00',
                      },
                      {
                        name: 'Up To 10,000 Contacts: AU $282.00',
                        value: '10000-282.00',
                      },
                      {
                        name: 'Up To 15,000 Contacts: AU $389.00',
                        value: '15000-389.00',
                      },
                      {
                        name: 'Up To 25,000 Contacts: AU $496.00',
                        value: '25000-496.00',
                      },
                      {
                        name: 'Up To 50,000 Contacts: AU $567.00',
                        value: '50000-567.00',
                      },
                      {
                        name: 'Up To 75,000 Contacts: AU $707.00',
                        value: '75000-707.00',
                      },
                      {
                        name: 'Up To 100,000 Contacts: AU $850.00',
                        value: '100000-850.00',
                      },
                      {
                        name: 'Up To 200,000 Contacts: AU $1,415.00',
                        value: '200000-1415.00',
                      },
                      {
                        name: 'Up To 500,000 Contacts: AU $1,865.00',
                        value: '500000-1865.00',
                      },
                      {
                        name: 'Up To 750,000 Contacts: AU $3,458.00',
                        value: '750000-3458.00',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: AU $4,376.00',
                        value: '1000000-4376.00',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: AU $6,989.00',
                        value: '2000000-6989.00',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: AU $8,471.00',
                        value: '3000000-8471.00',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: AU $9,953.00',
                        value: '4000000-9953.00',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: AU $11,930.00',
                        value: '5000000-11930.00',
                      },
                    ]
                  : selectedCountry === 'NZ'
                  ? [
                      {
                        name: 'Up To 1,000 Contacts: $46.00',
                        value: '1000-46.00',
                      },
                      {
                        name: 'Up To 2,500 Contacts: $108.00',
                        value: '2500-108.00',
                      },
                      {
                        name: 'Up To 5,000 Contacts: $196.00',
                        value: '5000-196.00',
                      },
                      {
                        name: 'Up To 10,000 Contacts: $310.00',
                        value: '10000-310.00',
                      },
                      {
                        name: 'Up To 15,000 Contacts: $428.00',
                        value: '15000-428.00',
                      },
                      {
                        name: 'Up To 25,000 Contacts: $568.00',
                        value: '25000-568.00',
                      },
                      {
                        name: 'Up To 50,000 Contacts: $624.00',
                        value: '50000-624.00',
                      },
                      {
                        name: 'Up To 75,000 Contacts: $778.00',
                        value: '75000-778.00',
                      },
                      {
                        name: 'Up To 100,000 Contacts: $935.00',
                        value: '100000-935.00',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $1,557.00',
                        value: '200000-1557.00',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $2,052.00',
                        value: '500000-2052.00',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $3,804.00',
                        value: '750000-3804.00',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $4,814.00',
                        value: '1000000-4814.00',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $7,689.00',
                        value: '2000000-7689.00',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $9,319.00',
                        value: '3000000-9319.00',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $10,949.00',
                        value: '4000000-10949.00',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $13,124.00',
                        value: '5000000-13124.00',
                      },
                    ]
                  : [
                      {
                        name: 'Up To 1,000 Contacts: £29.00',
                        value: '1000-29.00',
                      },
                      {
                        name: 'Up To 2,500 Contacts: £68.00',
                        value: '2500-68.00',
                      },
                      {
                        name: 'Up To 5,000 Contacts: £122.00',
                        value: '5000-122.00',
                      },
                      {
                        name: 'Up To 10,000 Contacts: £196.00',
                        value: '10000-196.00',
                      },
                      {
                        name: 'Up To 15,000 Contacts: £269.00',
                        value: '15000-269.00',
                      },
                      {
                        name: 'Up To 25,000 Contacts: £342.00',
                        value: '25000-342.00',
                      },
                      {
                        name: 'Up To 50,000 Contacts: £390.00',
                        value: '50000-390.00',
                      },
                      {
                        name: 'Up To 75,000 Contacts: £488.00',
                        value: '75000-488.00',
                      },
                      {
                        name: 'Up To 100,000 Contacts: £586.00',
                        value: '100000-586.00',
                      },
                      {
                        name: 'Up To 200,000 Contacts: £976.00',
                        value: '200000-976.00',
                      },
                      {
                        name: 'Up To 500,000 Contacts: £1,609.00',
                        value: '500000-1609.00',
                      },
                      {
                        name: 'Up To 750,000 Contacts: £2,390.00',
                        value: '750000-2390.00',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: £3,024.00',
                        value: '1000000-3024.00',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: £4,829.00',
                        value: '2000000-4829.00',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: £5,854.00',
                        value: '3000000-5854.00',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: £6,878.00',
                        value: '4000000-6878.00',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: £8,244.00',
                        value: '5000000-8244.00',
                      },
                    ]
              let KEAP_TEXT_VOICE_OPTIONS =
                selectedCountry === 'US'
                  ? [
                      {name: 'Tier 1: $14', value: '1-14'},
                      {name: 'Tier 2: $24', value: '2-24'},
                      {name: 'Tier 3: $39', value: '3-39'},
                      {name: 'Tier 4: $79', value: '4-79'},
                      {name: 'Tier 5: $139', value: '5-139'},
                      {name: 'Tier 6: $279', value: '6-279'},
                    ]
                  : selectedCountry === 'CA'
                  ? [
                      {name: 'Tier 1: $19', value: '1-19'},
                      {name: 'Tier 2: $32', value: '2-32'},
                      {name: 'Tier 3: $53', value: '3-53'},
                      {name: 'Tier 4: $107', value: '4-107'},
                      {name: 'Tier 5: $188', value: '5-188'},
                      {name: 'Tier 6: $377', value: '6-377'},
                    ]
                  : selectedCountry === 'AU'
                  ? [
                      {name: 'Tier 1: AU $21 w/GST', value: '1-21'},
                      {
                        name: 'Tier 2: AU $40.00',
                        value: '2-40.00',
                      },
                      {
                        name: 'Tier 3: AU $65.00',
                        value: '3-65.00',
                      },
                      {
                        name: 'Tier 4: AU $130.00',
                        value: '4-130.00',
                      },
                      {name: 'Tier 5: AU $230.00', value: '5-230.00'},
                      {
                        name: 'Tier 6: AU $465.00',
                        value: '6-465.00',
                      },
                    ]
                  : selectedCountry === 'NZ'
                  ? [
                      {name: 'Tier 1: $23', value: '1-23'},
                      {name: 'Tier 2: $40.00', value: '2-40.00'},
                      {name: 'Tier 3: $65.00', value: '3-65.00'},
                      {name: 'Tier 4: $132.00', value: '4-132.00'},
                      {name: 'Tier 5: $232.00', value: '5-232.00'},
                      {name: 'Tier 6: $465.00', value: '6-465.00'},
                    ]
                  : [
                      {name: 'Tier 1: £11.00', value: '1-11.00'},
                      {name: 'Tier 2: £19.00', value: '2-19.00'},
                      {name: 'Tier 3: £31.00', value: '3-31.00'},
                      {name: 'Tier 4: £62.00', value: '4-62.00'},
                      {name: 'Tier 5: £108.00', value: '5-108.00'},
                      {name: 'Tier 6: £219.00', value: '6-219.00'},
                    ]
              let ADDITIONAL_CONTACT_OPTIONS_ANNUAL =
                selectedCountry === 'US'
                  ? [
                      {name: 'Up To 1,000 Contacts: $346', value: '1000-346'},
                      {name: 'Up To 2,500 Contacts: $792', value: '2500-792'},
                      {
                        name: 'Up To 5,000 Contacts: $1,440',
                        value: '5000-1440',
                      },
                      {
                        name: 'Up To 10,000 Contacts: $2,304',
                        value: '10000-2304',
                      },
                      {
                        name: 'Up To 15,000 Contacts: $3,168',
                        value: '15000-3168',
                      },
                      {
                        name: 'Up To 25,000 Contacts: $4,032',
                        value: '25000-4032',
                      },
                      {
                        name: 'Up To 50,000 Contacts: $4,608',
                        value: '50000-4608',
                      },
                      {
                        name: 'Up To 75,000 Contacts: $5,760',
                        value: '75000-5760',
                      },
                      {
                        name: 'Up To 100,000 Contacts: $6,912',
                        value: '100000-6912',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $11,520',
                        value: '200000-11520',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $19,008',
                        value: '500000-19008',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $28,224',
                        value: '750000-28224',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $35,712',
                        value: '1000000-35712',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $57,024',
                        value: '2000000-57024',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $69,120',
                        value: '3000000-69120',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $81,216',
                        value: '4000000-81216',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $97,344',
                        value: '5000000-97344',
                      },
                    ]
                  : selectedCountry === 'CA'
                  ? [
                      {name: 'Up To 1,000 Contacts: $470', value: '1000-470'},
                      {
                        name: 'Up To 2,500 Contacts: $1,094',
                        value: '2500-1094',
                      },
                      {
                        name: 'Up To 5,000 Contacts: $1,949',
                        value: '5000-1949',
                      },
                      {
                        name: 'Up To 10,000 Contacts: $3,120',
                        value: '10000-3120',
                      },
                      {
                        name: 'Up To 15,000 Contacts: $4,282',
                        value: '15000-4282',
                      },
                      {
                        name: 'Up To 25,000 Contacts: $5,462',
                        value: '25000-5462',
                      },
                      {
                        name: 'Up To 50,000 Contacts: $6,230',
                        value: '50000-6230',
                      },
                      {
                        name: 'Up To 75,000 Contacts: $7,786',
                        value: '75000-7786',
                      },
                      {
                        name: 'Up To 100,000 Contacts: $9,341',
                        value: '100000-9341',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $15,571',
                        value: '200000-15571',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $25,690',
                        value: '500000-25690',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $38,141',
                        value: '750000-38141',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $48,259',
                        value: '1000000-48259',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $77,059',
                        value: '2000000-77059',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $93,408',
                        value: '3000000-93408',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $109,757',
                        value: '4000000-109757',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $131,549',
                        value: '5000000-131549',
                      },
                    ]
                  : selectedCountry === 'AU'
                  ? [
                      {name: 'Up To 1,000 Contacts: $432', value: '1000-432'},
                      {name: 'Up To 2,500 Contacts: $972', value: '2500-972'},
                      {
                        name: 'Up To 5,000 Contacts: $1,775',
                        value: '5000-1775',
                      },
                      {
                        name: 'Up To 10,000 Contacts: $2,833',
                        value: '10000-2833',
                      },
                      {
                        name: 'Up To 15,000 Contacts: $3,900',
                        value: '15000-3900',
                      },
                      {
                        name: 'Up To 25,000 Contacts: $4,968',
                        value: '25000-4968',
                      },
                      {
                        name: 'Up To 50,000 Contacts: $5,676',
                        value: '50000-5676',
                      },
                      {
                        name: 'Up To 75,000 Contacts: $7,092',
                        value: '75000-7092',
                      },
                      {
                        name: 'Up To 100,000 Contacts: $8,507',
                        value: '100000-8507',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $14,185',
                        value: '200000-14185',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $23,400',
                        value: '500000-23400',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $34,740',
                        value: '750000-34740',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $43,956',
                        value: '1000000-43956',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $70,188',
                        value: '2000000-70188',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $85,067',
                        value: '3000000-85067',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $99,960',
                        value: '4000000-99960',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $119,808',
                        value: '5000000-119808',
                      },
                    ]
                  : selectedCountry === 'NZ'
                  ? [
                      {name: 'Up To 1,000 Contacts: $442', value: '1000-442'},
                      {
                        name: 'Up To 2,500 Contacts: $1,037',
                        value: '2500-1037',
                      },
                      {
                        name: 'Up To 5,000 Contacts: $1,882',
                        value: '5000-1882',
                      },
                      {
                        name: 'Up To 10,000 Contacts: $2,976',
                        value: '10000-2976',
                      },
                      {
                        name: 'Up To 15,000 Contacts: $4,109',
                        value: '15000-4109',
                      },
                      {
                        name: 'Up To 25,000 Contacts: $5,453',
                        value: '25000-5453',
                      },
                      {
                        name: 'Up To 50,000 Contacts: $5,990',
                        value: '50000-5990',
                      },
                      {
                        name: 'Up To 75,000 Contacts: $7,469',
                        value: '75000-7469',
                      },
                      {
                        name: 'Up To 100,000 Contacts: $8,976',
                        value: '100000-8976',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $14,947',
                        value: '200000-14947',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $19,699',
                        value: '500000-19699',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $36,518',
                        value: '750000-36518',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $46,214',
                        value: '1000000-46214',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $73,814',
                        value: '2000000-73814',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $89,462',
                        value: '3000000-89462',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $105,110',
                        value: '4000000-105110',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $125,990',
                        value: '5000000-125990',
                      },
                    ]
                  : [
                      {name: 'Up To 1,000 Contacts: £278', value: '1000-278'},
                      {name: 'Up To 2,500 Contacts: £653', value: '2500-653'},
                      {
                        name: 'Up To 5,000 Contacts: £1,171',
                        value: '5000-1171',
                      },
                      {
                        name: 'Up To 10,000 Contacts: £1,872',
                        value: '10000-1872',
                      },
                      {
                        name: 'Up To 15,000 Contacts: £2,582',
                        value: '25000-2582',
                      },
                      {
                        name: 'Up To 25,000 Contacts: £3,283',
                        value: '50000-3283',
                      },
                      {
                        name: 'Up To 50,000 Contacts: £3,744',
                        value: '75000-3744',
                      },
                      {
                        name: 'Up To 75,000 Contacts: £4,685',
                        value: '100000-4685',
                      },
                      {
                        name: 'Up To 100,000 Contacts: £5,626',
                        value: '200000-5626',
                      },
                      {
                        name: 'Up To 200,000 Contacts: £9,370',
                        value: '500000-9370',
                      },
                      {
                        name: 'Up To 500,000 Contacts: £15,446',
                        value: '750000-15446',
                      },
                      {
                        name: 'Up To 750,000 Contacts: £22,944',
                        value: '1000000-22944',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: £29,030',
                        value: '2000000-29030',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: £46,358',
                        value: '3000000-46358',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: £56,198',
                        value: '4000000-56198',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: £66,029',
                        value: '4000000-66029',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: £79,142',
                        value: '5000000-79142',
                      },
                    ]
              let KEAP_TEXT_VOICE_OPTIONS_ANNUAL =
                selectedCountry === 'US'
                  ? [
                      {name: 'Tier 1: $168', value: '1-168'},
                      {name: 'Tier 2: $288', value: '2-288'},
                      {name: 'Tier 3: $468', value: '3-468'},
                      {name: 'Tier 4: $948', value: '4-948'},
                      {name: 'Tier 5: $1,668', value: '5-1668'},
                      {name: 'Tier 6: $3,348', value: '6-3348'},
                    ]
                  : selectedCountry === 'CA'
                  ? [
                      {name: 'Tier 1: $228', value: '1-228'},
                      {name: 'Tier 2: $384', value: '2-384'},
                      {name: 'Tier 3: $636', value: '3-636'},
                      {name: 'Tier 4: $1,284', value: '4-1284'},
                      {name: 'Tier 5: $2,256', value: '5-2256'},
                      {name: 'Tier 6: $4,524', value: '6-4524'},
                    ]
                  : selectedCountry === 'NZ'
                  ? [
                      {name: 'Tier 1: $276', value: '1-276'},
                      {name: 'Tier 2: $288', value: '2-288'},
                      {name: 'Tier 3: $468', value: '3-468'},
                      {name: 'Tier 4: $948', value: '4-948'},
                      {name: 'Tier 5: $1,668', value: '5-1668'},
                      {name: 'Tier 6: $3,348', value: '6-3348'},
                    ]
                  : selectedCountry === 'AU'
                  ? [
                      {name: 'Tier 1: $252', value: '1-252'},
                      {name: 'Tier 2: $480', value: '2-480'},
                      {name: 'Tier 3: $780', value: '3-780'},
                      {name: 'Tier 4: $1,560', value: '4-1560'},
                      {name: 'Tier 5: $2,760', value: '5-2760'},
                      {name: 'Tier 6: $5,580', value: '6-5580'},
                    ]
                  : [
                      {name: 'Tier 1: £132', value: '1-132'},
                      {name: 'Tier 2: £228', value: '2-228'},
                      {name: 'Tier 3: £372', value: '3-372'},
                      {name: 'Tier 4: £744', value: '4-744'},
                      {name: 'Tier 5: £1,296', value: '5-1296'},
                      {name: 'Tier 6: £2,628', value: '6-2628'},
                    ]

              let ADDITIONAL_CONTACT_OPTIONS_GRAPHLY_ANNUAL =
                selectedCountry === 'US'
                  ? [
                      {name: 'Up To 1,000 Contacts: $102', value: '1000-102'},
                      {name: 'Up To 2,500 Contacts: $153', value: '2500-153'},
                      {name: 'Up To 5,000 Contacts: $204', value: '5000-204'},
                      {name: 'Up To 10,000 Contacts: $306', value: '10000-306'},
                      {name: 'Up To 25,000 Contacts: $510', value: '25000-510'},
                      {name: 'Up To 50,000 Contacts: $816', value: '50000-816'},
                      {
                        name: 'Up To 75,000 Contacts: $1,020',
                        value: '75000-1020',
                      },
                      {
                        name: 'Up To 100,000 Contacts: $1,224',
                        value: '100000-1224',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $1,663.62',
                        value: '200000-1663.62',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $3,570',
                        value: '500000-3570',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $5,100',
                        value: '750000-5100',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $7,140',
                        value: '1000000-7140',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $9,690',
                        value: '2000000-9690',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $11,730',
                        value: '3000000-11730',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $13,260',
                        value: '4000000-13260',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $14,280',
                        value: '5000000-14280',
                      },
                    ]
                  : selectedCountry === 'CA'
                  ? [
                      {
                        name: 'Up To 1,000 Contacts: $142.80',
                        value: '1000-142.80',
                      },
                      {name: 'Up To 2,500 Contacts: $204', value: '2500-204'},
                      {
                        name: 'Up To 5,000 Contacts: $285.50',
                        value: '5000-285.60',
                      },
                      {
                        name: 'Up To 10,000 Contacts: $418.20',
                        value: '10000-418.20',
                      },
                      {name: 'Up To 25,000 Contacts: $714', value: '25000-714'},
                      {
                        name: 'Up To 50,000 Contacts: $1,122',
                        value: '50000-1122',
                      },
                      {
                        name: 'Up To 75,000 Contacts: $1,407',
                        value: '75000-1407',
                      },
                      {
                        name: 'Up To 100,000 Contacts: $1,693.20',
                        value: '100000-1693.20',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $2,805',
                        value: '200000-2805',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $4,926.40',
                        value: '500000-4926.60',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $7,038',
                        value: '750000-7038',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $9,863.40',
                        value: '1000000-9863.40',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $13,382.40',
                        value: '2000000-13382.40',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $16,197.60',
                        value: '3000000-16197.60',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $18,309',
                        value: '4000000-18309',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $19,716',
                        value: '5000000-19716',
                      },
                    ]
                  : selectedCountry === 'AU'
                  ? [
                      {
                        name: 'Up To 1,000 Contacts: $165.30',
                        value: '1000-165.30',
                      },
                      {
                        name: 'Up To 2,500 Contacts: $247.95',
                        value: '2500-247.95',
                      },
                      {
                        name: 'Up To 5,000 Contacts: $330.61',
                        value: '5000-330.61',
                      },
                      {
                        name: 'Up To 10,000 Contacts: $495.90',
                        value: '10000-495.90',
                      },
                      {
                        name: 'Up To 25,000 Contacts: $826.52',
                        value: '25000-826.52',
                      },
                      {
                        name: 'Up To 50,000 Contacts: $1,322.43',
                        value: '50000-1322.43',
                      },
                      {
                        name: 'Up To 75,000 Contacts: $1,653.04',
                        value: '75000-1653.04',
                      },
                      {
                        name: 'Up To 100,000 Contacts: $1,983.64',
                        value: '100000-1983.64',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $3,306.08',
                        value: '200000-3306.08',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $5,785.65',
                        value: '500000-5785.65',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $8,265.21',
                        value: '750000-8265.21',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $11,571.30',
                        value: '1000000-11571.30',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $15,703.90',
                        value: '2000000-15703.90',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $19,009.98',
                        value: '3000000-19009.98',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $21,489.55',
                        value: '4000000-21489.55',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $23,142.59',
                        value: '5000000-23142.59',
                      },
                    ]
                  : selectedCountry === 'NZ'
                  ? [
                      {
                        name: 'Up To 1,000 Contacts: $165.27',
                        value: '1000-165.27',
                      },
                      {
                        name: 'Up To 2,500 Contacts: $247.85',
                        value: '2500-247.85',
                      },
                      {
                        name: 'Up To 5,000 Contacts: $330.54',
                        value: '5000-330.54',
                      },
                      {
                        name: 'Up To 10,000 Contacts: $495.81',
                        value: '10000-495.81',
                      },
                      {
                        name: 'Up To 25,000 Contacts: $826.47',
                        value: '25000-826.47',
                      },
                      {
                        name: 'Up To 50,000 Contacts: $1,322.39',
                        value: '50000-1322.39',
                      },
                      {
                        name: 'Up To 75,000 Contacts: $1,653.04',
                        value: '75000-1653.04',
                      },
                      {
                        name: 'Up To 100,000 Contacts: $1,983.58',
                        value: '100000-1983.58',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $3,306.09',
                        value: '200000-3306.09',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $5,785.59',
                        value: '500000-5785.59',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $8,265.21',
                        value: '750000-8265.21',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $11,571.30',
                        value: '1000000-11571.30',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $15,703.85',
                        value: '2000000-15703.85',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $19,009.93',
                        value: '3000000-19009.93',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $21,489.55',
                        value: '4000000-21489.55',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $23,142.82',
                        value: '5000000-23142.82',
                      },
                    ]
                  : [
                      {
                        name: 'Up To 1,000 Contacts: £83.18',
                        value: '1000-83.18',
                      },
                      {
                        name: 'Up To 2,500 Contacts: £124.77',
                        value: '2500-124.77',
                      },
                      {
                        name: 'Up To 5,000 Contacts: £166.36',
                        value: '5000-166.36',
                      },
                      {
                        name: 'Up To 10,000 Contacts: £249.54',
                        value: '10000-249.54',
                      },
                      {
                        name: 'Up To 25,000 Contacts: £415.90',
                        value: '25000-415.90',
                      },
                      {
                        name: 'Up To 50,000 Contacts: £665.44',
                        value: '50000-665.44',
                      },
                      {
                        name: 'Up To 75,000 Contacts: £831.81',
                        value: '75000-831.81',
                      },
                      {
                        name: 'Up To 100,000 Contacts: £998.17',
                        value: '100000-998.17',
                      },
                      {
                        name: 'Up To 200,000 Contacts: £2,040',
                        value: '200000-2040',
                      },
                      {
                        name: 'Up To 500,000 Contacts: £2,911.33',
                        value: '500000-2911.33',
                      },
                      {
                        name: 'Up To 750,000 Contacts: £4,159.05',
                        value: '750000-4159.05',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: £5,822.67',
                        value: '1000000-5822.67',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: £7,902.19',
                        value: '2000000-7902.19',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: £9,566.81',
                        value: '3000000-9566.81',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: £10,813.53',
                        value: '4000000-10813.53',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: £11,645.34',
                        value: '5000000-11645.34',
                      },
                    ]

              let PLUS_THIS_PREMIUM_ANNUAL =
                selectedCountry === 'US'
                  ? [
                      {
                        name: 'Up To 200,000 Transactions: $2,388',
                        value: '200000-2388',
                      },
                      {
                        name: 'Up To 500,000 Transactions: $3,588',
                        value: '500000-3588',
                      },
                      {
                        name: 'Up To 1,000,000 Transactions: $5,988',
                        value: '1000000-5988',
                      },
                      {
                        name: 'Up To 2,000,000 Transactions: $8,388',
                        value: '2000000-8388',
                      },
                    ]
                  : selectedCountry === 'CA'
                  ? (PLUS_THIS_PREMIUM_ANNUAL = [
                      {
                        name: 'Up To 200,000 Transactions: $3,228',
                        value: '200000-3288',
                      },
                      {
                        name: 'Up To 500,000 Transactions: $4,860',
                        value: '500000-4860',
                      },
                      {
                        name: 'Up To 1,000,000 Transactions: $8,400',
                        value: '1000000-8400',
                      },
                      {
                        name: 'Up To 2,000,000 Transactions: $11,580',
                        value: '2000000-11580',
                      },
                    ])
                  : selectedCountry === 'AU'
                  ? (PLUS_THIS_PREMIUM_ANNUAL = [
                      {
                        name: 'Up To 200,000 Transactions: $3,876',
                        value: '200000-3876',
                      },
                      {
                        name: 'Up To 500,000 Transactions: $5,016',
                        value: '500000-5016',
                      },
                      {
                        name: 'Up To 1,000,000 Transactions: $8,400',
                        value: '1000000-8400',
                      },
                      {
                        name: 'Up To 2,000,000 Transactions: $11,760',
                        value: '2000000-11760',
                      },
                    ])
                  : selectedCountry === 'NZ'
                  ? (PLUS_THIS_PREMIUM_ANNUAL = [
                      {
                        name: 'Up To 200,000 Transactions: $3,894',
                        value: '200000-3894',
                      },
                      {
                        name: 'Up To 500,000 Transactions: $5,544',
                        value: '500000-5544',
                      },
                      {
                        name: 'Up To 1,000,000 Transactions: $9,240',
                        value: '1000000-9240',
                      },
                      {
                        name: 'Up To 2,000,000 Transactions: $12,936',
                        value: '2000000-12936',
                      },
                    ])
                  : (PLUS_THIS_PREMIUM_ANNUAL = [
                      {
                        name: 'Up To 200,000 Transactions: £1,620',
                        value: '200000-1620',
                      },
                      {
                        name: 'Up To 500,000 Transactions: £2,676',
                        value: '500000-2676',
                      },
                      {
                        name: 'Up To 1,000,000 Transactions: £4,464',
                        value: '1000000-4464',
                      },
                      {
                        name: 'Up To 2,000,000 Transactions: £6,240',
                        value: '2000000-6240',
                      },
                    ])

              const ADDITIONAL_CONTACT_OPTIONS_GRAPHLY_M2M =
                selectedCountry === 'US'
                  ? [
                      {name: 'Up To 1,000 Contacts: $10', value: '1000-10'},
                      {name: 'Up To 2,500 Contacts: $15', value: '2500-15'},
                      {name: 'Up To 5,000 Contacts: $20', value: '5000-20'},
                      {name: 'Up To 10,000 Contacts: $30', value: '10000-30'},
                      {name: 'Up To 25,000 Contacts: $50', value: '25000-50'},
                      {name: 'Up To 50,000 Contacts: $80', value: '50000-80'},
                      {name: 'Up To 75,000 Contacts: $100', value: '75000-100'},
                      {
                        name: 'Up To 100,000 Contacts: $120',
                        value: '100000-120',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $200',
                        value: '200000-200',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $350',
                        value: '500000-350',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $500',
                        value: '750000-500',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $700',
                        value: '1000000-700',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $950',
                        value: '2000000-950',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $1,150',
                        value: '3000000-1150',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $1,300',
                        value: '4000000-1300',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $1,400',
                        value: '5000000-1400',
                      },
                    ]
                  : selectedCountry === 'CA'
                  ? [
                      {name: 'Up To 1,000 Contacts: $14', value: '1000-14'},
                      {name: 'Up To 2,500 Contacts: $20', value: '2500-20'},
                      {name: 'Up To 5,000 Contacts: $28', value: '5000-28'},
                      {name: 'Up To 10,000 Contacts: $41', value: '10000-41'},
                      {name: 'Up To 25,000 Contacts: $70', value: '25000-70'},
                      {name: 'Up To 50,000 Contacts: $110', value: '50000-110'},
                      {name: 'Up To 75,000 Contacts: $138', value: '75000-138'},
                      {
                        name: 'Up To 100,000 Contacts: $166',
                        value: '100000-166',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $275',
                        value: '200000-275',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $483',
                        value: '500000-483',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $690',
                        value: '750000-690',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $967',
                        value: '1000000-967',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $1,312',
                        value: '2000000-1312',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $1,588',
                        value: '3000000-1588',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $1,795',
                        value: '4000000-1795',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $1,933',
                        value: '5000000-1933',
                      },
                    ]
                  : selectedCountry === 'AU'
                  ? [
                      {
                        name: 'Up To 1,000 Contacts: $16.20',
                        value: '1000-16.20',
                      },
                      {
                        name: 'Up To 2,500 Contacts: $24.30',
                        value: '2500-24.30',
                      },
                      {
                        name: 'Up To 5,000 Contacts: $32.41',
                        value: '5000-32.41',
                      },
                      {
                        name: 'Up To 10,000 Contacts: $48.61',
                        value: '10000-48.61',
                      },
                      {
                        name: 'Up To 25,000 Contacts: $81.03',
                        value: '25000-81.03',
                      },
                      {
                        name: 'Up To 50,000 Contacts: $129.65',
                        value: '50000-129.65',
                      },
                      {
                        name: 'Up To 75,000 Contacts: $162.06',
                        value: '75000-162.06',
                      },
                      {
                        name: 'Up To 100,000 Contacts: $194.47',
                        value: '100000-194.47',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $324.13',
                        value: '200000-324.13',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $567.21',
                        value: '500000-567.21',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $810.32',
                        value: '750000-810.32',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $1,134.44',
                        value: '1000000-1134.44',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $1,539.59',
                        value: '2000000-1539.59',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $1,863.72',
                        value: '3000000-1863.72',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $2,106.82',
                        value: '4000000-2106.82',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $2,269.98',
                        value: '5000000-2269.98',
                      },
                    ]
                  : selectedCountry === 'NZ'
                  ? [
                      {
                        name: 'Up To 1,000 Contacts: $16.20',
                        value: '1000-16.20',
                      },
                      {
                        name: 'Up To 2,500 Contacts: $24.30',
                        value: '2500-24.30',
                      },
                      {
                        name: 'Up To 5,000 Contacts: $32.41',
                        value: '5000-32.41',
                      },
                      {
                        name: 'Up To 10,000 Contacts: $48.61',
                        value: '10000-48.61',
                      },
                      {
                        name: 'Up To 25,000 Contacts: $81.03',
                        value: '25000-81.03',
                      },
                      {
                        name: 'Up To 50,000 Contacts: $129.65',
                        value: '50000-129.65',
                      },
                      {
                        name: 'Up To 75,000 Contacts: $162.06',
                        value: '75000-162.06',
                      },
                      {
                        name: 'Up To 100,000 Contacts: $194.47',
                        value: '100000-194.47',
                      },
                      {
                        name: 'Up To 200,000 Contacts: $324.13',
                        value: '200000-324.13',
                      },
                      {
                        name: 'Up To 500,000 Contacts: $567.22',
                        value: '500000-567.22',
                      },
                      {
                        name: 'Up To 750,000 Contacts: $810.32',
                        value: '750000-810.32',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: $1,134.44',
                        value: '1000000-1134.44',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: $1,539.59',
                        value: '2000000-1539.59',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: $1,863.72',
                        value: '3000000-1863.72',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: $2,106.82',
                        value: '4000000-2106.82',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: $2,269.98',
                        value: '5000000-2269.98',
                      },
                    ]
                  : [
                      {name: 'Up To 1,000 Contacts: £8.15', value: '1000-8.15'},
                      {
                        name: 'Up To 2,500 Contacts: £12.23',
                        value: '2500-12.23',
                      },
                      {
                        name: 'Up To 5,000 Contacts: £16.31',
                        value: '5000-16.31',
                      },
                      {
                        name: 'Up To 10,000 Contacts: £24.46',
                        value: '10000-24.46',
                      },
                      {
                        name: 'Up To 25,000 Contacts: £40.77',
                        value: '25000-40.77',
                      },
                      {
                        name: 'Up To 50,000 Contacts: £65.24',
                        value: '50000-65.24',
                      },
                      {
                        name: 'Up To 75,000 Contacts: £81.55',
                        value: '75000-81.55',
                      },
                      {
                        name: 'Up To 100,000 Contacts: £97.86',
                        value: '100000-97.86',
                      },
                      {
                        name: 'Up To 200,000 Contacts: £163.10',
                        value: '200000-163.10',
                      },
                      {
                        name: 'Up To 500,000 Contacts: £285.42',
                        value: '500000-285.42',
                      },
                      {
                        name: 'Up To 750,000 Contacts: £407.75',
                        value: '750000-407.75',
                      },
                      {
                        name: 'Up To 1,000,000 Contacts: £570.85',
                        value: '1000000-570.85',
                      },
                      {
                        name: 'Up To 2,000,000 Contacts: £774.72',
                        value: '2000000-774.72',
                      },
                      {
                        name: 'Up To 3,000,000 Contacts: £937.82',
                        value: '3000000-937.82',
                      },
                      {
                        name: 'Up To 4,000,000 Contacts: £1,060.15',
                        value: '4000000-1060.15',
                      },
                      {
                        name: 'Over 4,000,000 Contacts: £1,141.70',
                        value: '5000000-1141.70',
                      },
                    ]

              const PLUS_THIS_PREMIUM_M2M =
                selectedCountry === 'US'
                  ? [
                      {
                        name: 'Up To 200,000 Transactions: $199',
                        value: '200000-199',
                      },
                      {
                        name: 'Up To 500,000 Transactions: $299',
                        value: '500000-299',
                      },
                      {
                        name: 'Up To 1,000,000 Transactions: $499',
                        value: '1000000-499',
                      },
                      {
                        name: 'Up To 2,000,000 Transactions: $699',
                        value: '2000000-699',
                      },
                    ]
                  : selectedCountry === 'CA'
                  ? [
                      {
                        name: 'Up To 200,000 Transactions: $269',
                        value: '200000-269',
                      },
                      {
                        name: 'Up To 500,000 Transactions: $405',
                        value: '500000-405',
                      },
                      {
                        name: 'Up To 1,000,000 Transactions: $700',
                        value: '1000000-700',
                      },
                      {
                        name: 'Up To 2,000,000 Transactions: $965',
                        value: '2000000-965',
                      },
                    ]
                  : selectedCountry === 'AU'
                  ? [
                      {
                        name: 'Up To 200,000 Transactions: $323',
                        value: '200000-323',
                      },
                      {
                        name: 'Up To 500,000 Transactions: $418',
                        value: '500000-418',
                      },
                      {
                        name: 'Up To 1,000,000 Transactions: $700',
                        value: '1000000-700',
                      },
                      {
                        name: 'Up To 2,000,000 Transactions: $980',
                        value: '2000000-980',
                      },
                    ]
                  : selectedCountry === 'NZ'
                  ? [
                      {
                        name: 'Up To 200,000 Transactions: $332',
                        value: '200000-332',
                      },
                      {
                        name: 'Up To 500,000 Transactions: $462',
                        value: '500000-462',
                      },
                      {
                        name: 'Up To 1,000,000 Transactions: $770',
                        value: '1000000-770',
                      },
                      {
                        name: 'Up To 2,000,000 Transactions: $1,078',
                        value: '2000000-1078',
                      },
                    ]
                  : [
                      {
                        name: 'Up To 200,000 Transactions: £135',
                        value: '200000-135',
                      },
                      {
                        name: 'Up To 500,000 Transactions: £223',
                        value: '500000-223',
                      },
                      {
                        name: 'Up To 1,000,000 Transactions: £372',
                        value: '1000000-372',
                      },
                      {
                        name: 'Up To 2,000,000 Transactions: £520',
                        value: '2000000-520',
                      },
                    ]

              let KCP_OPTIONS =
                selectedCountry === 'US'
                  ? [
                      {name: '$1500', value: '1500'},
                      {name: '$750', value: '750'},
                      {name: '$499', value: '499'},
                      {name: '$299', value: '299'},
                    ]
                  : selectedCountry === 'CA'
                  ? [
                      {name: '$405', value: '405'},
                      {name: '$700', value: '700'},
                      {name: '$1000', value: '1000'},
                      {name: '$2000', value: '2000'},
                    ]
                  : selectedCountry === 'AU'
                  ? [
                      {name: '$418', value: '418'},
                      {name: '$704', value: '704'},
                      {name: '$1001', value: '1001'},
                      {name: '$2002', value: '2002'},
                    ]
                  : selectedCountry === 'NZ'
                  ? [
                      {name: '$462', value: '462'},
                      {name: '$770', value: '770'},
                      {name: '$1100', value: '1100'},
                      {name: '$2200', value: '2200'},
                    ]
                  : [
                      {name: '£223', value: '223'},
                      {name: '£372', value: '372'},
                      {name: '£559', value: '559'},
                      {name: '£1116', value: '1116'},
                    ]

              const parseKeapTierOption = option => {
                const [qtyRaw, amountRaw = ''] = String(
                  option?.value || '',
                ).split('-')
                const qty = Number(qtyRaw)
                const amount = Number(String(amountRaw).replace(/,/g, ''))
                if (!Number.isFinite(qty) || !Number.isFinite(amount))
                  return null
                return {...option, qty, amount}
              }

              const resolveKeapTierOption = (options = [], addon = null) => {
                if (!addon || !Array.isArray(options) || options.length === 0)
                  return null
                const parsedOptions = options
                  .map(parseKeapTierOption)
                  .filter(Boolean)
                if (parsedOptions.length === 0) return null

                const unitAmount = Number(addon.unitAmount)
                const subtotal = Number(addon.subtotal)
                const candidates = [unitAmount, subtotal].filter(
                  Number.isFinite,
                )
                const exactMatchTolerance = 0.2

                for (const candidate of candidates) {
                  const exactMatch = parsedOptions.find(
                    option =>
                      Math.abs(Number(option.amount) - Number(candidate)) <=
                      exactMatchTolerance,
                  )
                  if (exactMatch) return exactMatch
                }

                const baseAmount = Number.isFinite(subtotal)
                  ? subtotal
                  : unitAmount
                if (Number.isFinite(baseAmount) && baseAmount > 0) {
                  const nearest = parsedOptions.reduce((best, option) => {
                    const distance = Math.abs(option.amount - baseAmount)
                    if (!best || distance < best.distance) {
                      return {...option, distance}
                    }
                    return best
                  }, null)

                  if (nearest) {
                    const relativeDistance = nearest.distance / baseAmount
                    if (relativeDistance <= 0.12) return nearest
                  }
                }

                // Fallback: use tierQuantity (Recurly's tier identifier) when price matching fails.
                // For "Over 4,000,000" the tier may use endingQuantity 999999999; price matching
                // can fail due to currency/rounding. Map tierQuantity to our option qty.
                const tierQuantity = Number(addon.tierQuantity)
                if (Number.isFinite(tierQuantity) && tierQuantity > 0) {
                  const optionsAtOrBelowTier = parsedOptions
                    .filter(o => o.qty <= tierQuantity)
                    .sort((a, b) => b.qty - a.qty)
                  const fallback =
                    optionsAtOrBelowTier[0] ||
                    parsedOptions.sort((a, b) => b.qty - a.qty)[0]
                  if (fallback) return fallback
                }

                return null
              }

              let kcpPlan = response.childSubscriptions.find(
                sub => sub.code === 'keap_kcp-cert-5-fee-annual',
              )
              if (kcpPlan) {
                console.log('KCP_OPTIONS ::', KCP_OPTIONS)
                console.log('setKcpRecurlyOption ::', kcpRecurlyOption)
                setKcpRecurlyOption(kcpPlan.quantity)
              }

              let kccPlan = response.childSubscriptions.find(
                sub => sub.code === 'keap_kcc-cert-5-fee-annual',
              )
              if (kccPlan) {
                setKccRecurlyOption(kccPlan.quantity)
                setQuantity(kccPlan.quantity)
              }

              let KeapAddOns = response.childSubscriptions.find(
                sub => sub.code === 'addon-5-keap-addons',
              )
              if (KeapAddOns) {
                let keapAdditionalContacts = KeapAddOns?.addons?.find(
                  addon => addon.code === 'addon-5-keap-addcontacts-m2m',
                )
                if (keapAdditionalContacts) {
                  const matchedAddContactOption = resolveKeapTierOption(
                    ADDITIONAL_CONTACT_OPTIONS,
                    keapAdditionalContacts,
                  )
                  if (matchedAddContactOption) {
                    setKeapAddContactsRecurlyQty(matchedAddContactOption.qty)
                    setKeapAddContactsQty(matchedAddContactOption.qty)
                    setSelectedAdditionalContactOption({
                      name: matchedAddContactOption.name,
                      value: matchedAddContactOption.value,
                    })
                  }
                }
                let keapTextVoice = KeapAddOns?.addons?.find(
                  addon => addon.code === 'addon-5-keap-textvoice-m2m',
                )
                if (keapTextVoice) {
                  const matchedKeapTextVoiceOption = resolveKeapTierOption(
                    KEAP_TEXT_VOICE_OPTIONS,
                    keapTextVoice,
                  )
                  if (matchedKeapTextVoiceOption) {
                    setKeapTextVoiceRecurlyQty(matchedKeapTextVoiceOption.qty)
                    setKeapTextVoiceQty(matchedKeapTextVoiceOption.qty)
                    setSelectedKeapTextVoice({
                      name: matchedKeapTextVoiceOption.name,
                      value: matchedKeapTextVoiceOption.value,
                    })
                  }
                }
                let KeapAddUsers = KeapAddOns?.addons?.find(
                  addon => addon.code === 'addon-5-keap-addusers-m2m',
                )
                if (KeapAddUsers) {
                  setKeapAddUsersRecurlyQty(KeapAddUsers.quantity)
                  setQuantity(KeapAddUsers.quantity)
                }
              }

              let KeapAddOnsAnnual = response.childSubscriptions.find(
                sub => sub.code === 'addon-5-keap-addons-annual',
              )
              if (KeapAddOnsAnnual) {
                let keapAdditionalContactsAnnual =
                  KeapAddOnsAnnual?.addons?.find(
                    addon => addon.code === 'addon-5-keap-addcontacts-annual',
                  )
                if (keapAdditionalContactsAnnual) {
                  const matchedAddContactOptionAnnual = resolveKeapTierOption(
                    ADDITIONAL_CONTACT_OPTIONS_ANNUAL,
                    keapAdditionalContactsAnnual,
                  )
                  if (matchedAddContactOptionAnnual) {
                    setKeapAddContactsRecurlyQtyAnnual(
                      matchedAddContactOptionAnnual.qty,
                    )
                    setKeapAddContactsQtyAnnual(
                      matchedAddContactOptionAnnual.qty,
                    )
                    setSelectedAdditionalContactOptionAnnual({
                      name: matchedAddContactOptionAnnual.name,
                      value: matchedAddContactOptionAnnual.value,
                    })
                  }
                }

                let keapTextVoiceAnnual = KeapAddOnsAnnual?.addons?.find(
                  addon => addon.code === 'addon-5-keap-textvoice-annual',
                )
                if (keapTextVoiceAnnual) {
                  const matchedKeapTextVoiceOptionAnnual =
                    resolveKeapTierOption(
                      KEAP_TEXT_VOICE_OPTIONS_ANNUAL,
                      keapTextVoiceAnnual,
                    )
                  if (matchedKeapTextVoiceOptionAnnual) {
                    setKeapTextVoiceRecurlyQtyAnnual(
                      matchedKeapTextVoiceOptionAnnual.qty,
                    )
                    setKeapTextVoiceQtyAnnual(
                      matchedKeapTextVoiceOptionAnnual.qty,
                    )
                    setSelectedKeapTextVoiceAnnual({
                      name: matchedKeapTextVoiceOptionAnnual.name,
                      value: matchedKeapTextVoiceOptionAnnual.value,
                    })
                  }
                }

                let KeapAddUsersAnnual = KeapAddOnsAnnual?.addons?.find(
                  addon => addon.code === 'addon-5-keap-addusers-annual',
                )
                if (KeapAddUsersAnnual) {
                  setKeapAddUsersRecurlyQtyAnnual(KeapAddUsersAnnual.quantity)
                  setQuantity(KeapAddUsersAnnual.quantity)
                }
              }

              let KeapAddOnsLegacyAnnual = response.childSubscriptions.find(
                sub => sub.code === 'addon-5-keap-addons-legacy-annual',
              )

              if (KeapAddOnsLegacyAnnual) {
                let keapAdditionalContactsGraphlyAnnual =
                  KeapAddOnsLegacyAnnual?.addons?.find(
                    addon =>
                      addon.code ===
                      'addon-5-keap-legacy-graphly-contacts-annual',
                  )

                if (keapAdditionalContactsGraphlyAnnual) {
                  const matchedAddContactOptionAnnual = resolveKeapTierOption(
                    ADDITIONAL_CONTACT_OPTIONS_GRAPHLY_ANNUAL,
                    keapAdditionalContactsGraphlyAnnual,
                  )
                  if (matchedAddContactOptionAnnual) {
                    setKeapAddContactsRecurlyQtyGraphlyAnnual(
                      matchedAddContactOptionAnnual.qty,
                    )
                    setKeapAddContactsQtyGraphlyAnnual(
                      matchedAddContactOptionAnnual.qty,
                    )
                    setSelectedAdditionalContactOptionGraphlyAnnual({
                      name: matchedAddContactOptionAnnual.name,
                      value: matchedAddContactOptionAnnual.value,
                    })
                  }
                }

                let plusThisPremiumAnnual =
                  KeapAddOnsLegacyAnnual?.addons?.find(
                    addon =>
                      addon.code ===
                      'addon-5-keap-legacy-plusthis-premium-trans-annual',
                  )

                if (plusThisPremiumAnnual) {
                  const matchedOptionAnnual = resolveKeapTierOption(
                    PLUS_THIS_PREMIUM_ANNUAL,
                    plusThisPremiumAnnual,
                  )
                  if (matchedOptionAnnual) {
                    setPlusThisPremiumRecurlyQtyAnnual(matchedOptionAnnual.qty)
                  }
                }
              }

              let KeapAddOnsLegacyM2m = response.childSubscriptions.find(
                sub => sub.code === 'addon-5-keap-addons-legacy-m2m',
              )

              if (KeapAddOnsLegacyM2m) {
                let keapAdditionalContactsGraphlyM2m =
                  KeapAddOnsLegacyM2m?.addons?.find(
                    addon =>
                      addon.code === 'addon-5-keap-legacy-graphly-contacts-m2m',
                  )

                if (keapAdditionalContactsGraphlyM2m) {
                  const matchedAddContactOptionM2m = resolveKeapTierOption(
                    ADDITIONAL_CONTACT_OPTIONS_GRAPHLY_M2M,
                    keapAdditionalContactsGraphlyM2m,
                  )
                  if (matchedAddContactOptionM2m) {
                    setKeapAddContactsRecurlyQtyGraphlyM2m(
                      matchedAddContactOptionM2m.qty,
                    )
                    setKeapAddContactsQtyGraphlyM2m(
                      matchedAddContactOptionM2m.qty,
                    )
                    setSelectedAdditionalContactOptionGraphlyM2m({
                      name: matchedAddContactOptionM2m.name,
                      value: matchedAddContactOptionM2m.value,
                    })
                  }
                }

                let plusThisPremiumM2m = KeapAddOnsLegacyM2m?.addons?.find(
                  addon =>
                    addon.code ===
                    'addon-5-keap-legacy-plusthis-premium-trans-m2m',
                )

                if (plusThisPremiumM2m) {
                  const matchedOptionM2m = resolveKeapTierOption(
                    PLUS_THIS_PREMIUM_M2M,
                    plusThisPremiumM2m,
                  )
                  if (matchedOptionM2m) {
                    setPlusThisPremiumRecurlyQtyM2m(matchedOptionM2m.qty)
                  }
                }
              }
            } catch (ex) {
              console.log('Exception is :: ', ex)
            }
            setAccountSubscriptions(response)
            setAccountSubscriptionData(response)
            const {
              accountInfo: {
                businessAddress,
                firstName,
                lastName,
                company,
                industryId,
                email,
                abn,
                allowInvoice,
                eaid,
                hasPaidDesignFee,
                hasPaidOnboardingFee,
              },
              //accountInfo,
              childSubscriptions,
              parentSubscriptions,
              balanceInfo,
              recurlyAccount,
            } = response
            const responseBillTo =
              order?.billTo ||
              order?.accountInformation?.billTo ||
              getSubscriptionBillTo(response) ||
              accountInfo?.billTo
            console.log(
              'catalog fetchAccountSubscriptions - response-----------',
              response,
            )
            console.log({hasPaidOnboardingFee, hasPaidDesignFee})
            const alphaSEO = response.childSubscriptions.find(
              sub => sub.code === 'seobase-5-thryv-m2m',
            )
            // FULSAS-12507: capture the account's existing Thryv 2.0 (MC 2.0)
            // domain, if any, from its Recurly "Domain" custom field, so the
            // Leads Lift domain field can pre-populate once that add-on is
            // selected (see the isLeadLift effect in options.js).
            const mc2Subscription = [
              ...(response.childSubscriptions || []),
              ...(response.parentSubscriptions || []),
            ].find(sub => MC_2_0_PLANS.includes(sub.code))
            setExistingMc2DomainValue(
              getCustomFieldValue(mc2Subscription?.customFields, ['Domain']) ||
                '',
            )
            if (alphaSEO) {
              const alphaProposalId =
                alphaSEO.alphaProposalId ||
                getCustomFieldValue(alphaSEO.customFields, [
                  'SEO_Proposal_ID',
                ]) ||
                ''
              const alphaDomain =
                alphaSEO.alphaDomain ||
                getCustomFieldValue(alphaSEO.customFields, ['Domain']) ||
                ''
              const alphaExternalDomainValue =
                alphaSEO.isExternalDomain ??
                alphaSEO.externalDomain ??
                getCustomFieldValue(alphaSEO.customFields, [
                  'isExternalDomain',
                  'Is_External_Domain',
                  'externalDomain',
                ]) ??
                null
              const normalizedExternalDomainValue =
                typeof alphaExternalDomainValue === 'string'
                  ? alphaExternalDomainValue.toLowerCase()
                  : alphaExternalDomainValue
              let normalizedExternalDomain =
                normalizedExternalDomainValue === true ||
                normalizedExternalDomainValue === 'yes'
                  ? 'yes'
                  : normalizedExternalDomainValue === false ||
                    normalizedExternalDomainValue === 'no'
                  ? 'no'
                  : null
              if (!normalizedExternalDomain && alphaSEO.state === 'future') {
                normalizedExternalDomain = 'no'
              }
              const alphaStartDate =
                alphaSEO.startDate ||
                alphaSEO.start_date ||
                getCustomFieldValue(alphaSEO.customFields, [
                  'futureBillingDate',
                  'Future_Billing_Date',
                  'future_billing_date',
                  'startDate',
                  'start_date',
                ]) ||
                null
              setAlphaSEOFromRecurly({
                alphaProposalId,
                alphaDomain,
                alphaAmount: alphaSEO.quantity,
                // alphaCategories: alphaSEO.categories,
                // alphaGeos: alphaSEO.geos,
                alphaKeyCount:
                  alphaSEO.addons.filter(
                    addon => addon.code === 'addon-5-seokey-m2m',
                  ).length > 0
                    ? alphaSEO.addons.filter(
                        addon => addon.code === 'addon-5-seokey-m2m',
                      )[0].quantity
                    : 0,
                alphaPowerboostCount:
                  alphaSEO.addons.filter(
                    addon => addon.code === 'addon-5-seopb-m2m',
                  ).length > 0
                    ? alphaSEO.addons.filter(
                        addon => addon.code === 'addon-5-seopb-m2m',
                      )[0].quantity
                    : 0,
                alphaIsExternalDomain: normalizedExternalDomain,
                alphaStartDate: alphaStartDate
                  ? dayjs(alphaStartDate).format('YYYY-MM-DD hh:mm:ss')
                  : null,
              })
              setAlphaAmount(alphaSEO.quantity)
              setAlphaDomain(alphaDomain)
              setAlphaProposalId(alphaProposalId)
              setIsExternalDomain(normalizedExternalDomain)
              setFutureBillingDate(
                alphaStartDate
                  ? dayjs(alphaStartDate).format('YYYY-MM-DD hh:mm:ss')
                  : undefined,
              )
              // setCategories(alphaSEO.categories)
              // setGeos(alphaSEO.geos)
              setAlphaKeyCount(
                alphaSEO.addons.filter(
                  addon => addon.code === 'addon-5-seokey-m2m',
                ).length > 0
                  ? alphaSEO.addons.filter(
                      addon => addon.code === 'addon-5-seokey-m2m',
                    )[0].quantity
                  : 0,
              )
              setAlphaPowerboostCount(
                alphaSEO.addons.filter(
                  addon => addon.code === 'addon-5-seopb-m2m',
                ).length > 0
                  ? alphaSEO.addons.filter(
                      addon => addon.code === 'addon-5-seopb-m2m',
                    )[0].quantity
                  : 0,
              )
            }
            if (allowInvoice) {
              setAllowInvoice(allowInvoice)
              setCohortEaid(eaid)
            }
            if (isNewOrder) {
              let orderData5 = {
                order,
                subscriptions: {
                  childSubscriptions,
                  parentSubscriptions,
                },
                accountInfo: {
                  ...order.accountInformation,
                  ...response.accountInfo,
                  billTo: responseBillTo,
                  balanceInfo,
                  recurlyEmail: email,
                  recurlyFirst: firstName,
                  recurlyLast: lastName,
                  recurlyCompany: company,
                  abn: abn?.replaceAll(' ', ''),
                  billingAddress: {
                    ...businessAddress,
                    state: businessAddress.region,
                    zipCode: businessAddress.postalCode,
                  },
                },
                recurlyAccount: recurlyAccount,
              }
              setOrderData5CU(orderData5)
              // createOrder(response)
              // if (!isNewAccountOrderCreated) {
              //   captureOrder5NewUpgrade(
              //     'catalog-init-newAccOrder',
              //     'New',
              //     orderData5,
              //   )
              // }
            }
            if (isUpgrade) {
              console.log('entered here ============================>')
              let noPaidSubList = [
                'appmarket-5-thryv-m2m',
                'cc_basicpls-5-thryv-m2m',
              ]
              let noPaidAddonList = [
                'addon-5-signatures_usage',
                'app-websitebuilder',
              ]
              let orderAddonsList = []
              let orderSubList = []
              response.parentSubscriptions.map(item => {
                orderSubList.push(item.code)
                item.addons.map(addonItem => {
                  orderAddonsList.push(addonItem.code)
                  return addonItem.code
                })
                return item
              })
              response.childSubscriptions.map(item => {
                orderSubList.push(item.code)
                item.addons.map(addonItem => {
                  orderAddonsList.push(addonItem.code)
                  return addonItem.code
                })
                return item
              })

              let isNoPaidSubsConditionExist = orderSubList.every(element =>
                noPaidSubList.includes(element),
              )

              let isNoPaidAddonsConditionExist = orderAddonsList.every(
                element => noPaidAddonList.includes(element),
              )

              if (
                !isNoPaidSubsConditionExist ||
                !isNoPaidAddonsConditionExist
              ) {
                setHasKeapAccountAbility(false)
              }
              console.log('conditop=======>', {
                isNoPaidSubsConditionExist,
                isNoPaidAddonsConditionExist,
                orderSubList,
                orderAddonsList,
              })
              const tempOrderData5CU = {
                order,
                subscriptions: {
                  childSubscriptions,
                  parentSubscriptions,
                },
                accountInfo: {
                  ...order.accountInformation,
                  ...response.accountInfo,
                  billTo: responseBillTo,
                  balanceInfo,
                  recurlyEmail: email,
                  recurlyFirst: firstName,
                  recurlyLast: lastName,
                  recurlyCompany: company,
                  abn: abn?.replaceAll(' ', ''),
                  billingAddress: {
                    ...businessAddress,
                    state: businessAddress.region,
                    zipCode: businessAddress.postalCode,
                  },
                },
                recurlyAccount: recurlyAccount,
              }

              console.log('catalog isUpgrade - ordetData5cu', tempOrderData5CU)
              setAccountInfo({
                ...accountInfo,
                keapAccount: response?.accountInfo?.keapAccount,
                xpCode: response?.accountInfo?.xpCode,
                billTo: responseBillTo,
                balanceInfo,
                recurlyEmail: email,
                recurlyFirst: firstName,
                recurlyLast: lastName,
                recurlyCompany: company,
                abn: abn?.replaceAll(' ', ''),
                billingAddress: {
                  ...businessAddress,
                  state: businessAddress.region,
                  zipCode: businessAddress.postalCode,
                },
                // balanceInfo: response.balanceInfo,
              })
              setKeapAccount(response?.accountInfo?.keapAccount || false)
              setKeapID(response?.accountInfo?.keapID || '')
              setOrderData5CU(tempOrderData5CU)
              if (!accountInfo.billingAddress.country) setCurrentStep(0)
              if (industryId) setOrder({...order, template: industryId})
            } else {
              setAccountInfo({...accountInfo, balanceInfo})
              setKeapAccount(accountInfo?.keapAccount || false)
              setKeapID(accountInfo?.keapID || '')
            }
          } catch (err) {
            if (
              [404, 500].includes(err.status) ||
              err.message === 'missing subscription'
            ) {
              if (retriesAmount === 0) throw err
              setTimeout(
                () => fetchAccountSubscriptions(retriesAmount - 1),
                5000,
              )
            }
          }
        }
        fetchAccountSubscriptions()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useEffect(() => {
    formatSelectedCentersAndApps()
  }, [formatSelectedCentersAndApps])

  useEffect(() => {
    getPlansPrices()
  }, [getPlansPrices])

  useEffect(() => {
    if (!plans || !accountSubscriptions) {
      return
    }

    const updatePrice = (sub, isAddon) => {
      const current = flatPlans.find(plan => plan.code === sub.code)
      for (const addon of sub.addons || []) {
        updatePrice(addon, true)
      }
      const total =
        selectedCountry === 'AU' && !isAddon ? sub.total : sub.subtotal
      if (!current || !total) return
      if (current.code.includes('addon-socialads')) {
        return undefined
      }
      if (current.code.includes('addon-5-ai-webbuilder')) {
        return undefined
      }
      // Quantity-based addons: amount must stay as unit price.
      // formatPrice/calcTotal multiply by quantity; subtotal would cause double multiplication.
      if (
        current.code === 'addon-5-keap-addusers-m2m' ||
        current.code === 'addon-ctn-bninat-m2m' ||
        current.code === 'addon-ctn-bninat-annual'
      ) {
        const unitAmount = sub.unitAmount ?? sub.unit_amount
        if (
          unitAmount != null &&
          Number(current.amount) !== Number(unitAmount)
        ) {
          shouldUpdate = true
          current.amount = unitAmount
        }
        return
      }

      if (current.amount !== total) {
        shouldUpdate = true
        current.amount = total
      }
    }

    let shouldUpdate = false
    const plansCopy = structuredClone(plans)
    const flatPlans = [
      ...plansCopy.apps,
      ...plansCopy.centers,
      ...plansCopy.oneTimes,
    ]

    for (const sub of [
      ...accountSubscriptions.childSubscriptions,
      ...accountSubscriptions.parentSubscriptions,
    ]) {
      updatePrice(sub)
    }

    if (shouldUpdate) {
      setPlans(plansCopy)
    }
  }, [plans, accountSubscriptions, selectedCountry])

  const diableValidateAndProposalInput = false

  const hasPaidDesignFee =
    accountInfo.hasPaidDesignFee ||
    accountSubscriptionData?.accountInfo?.hasPaidDesignFee

  const isWebsiteAppAlreadyIncluded =
    defaultCentersAndApps?.current?.apps?.some(
      ({code}) => code === 'addon-5-website_difm',
    )
  const isWebsiteAddedAsEntitlement = selectedCentersAndApps?.centers?.some(
    ({entitlements}) =>
      entitlements?.some(({code}) => code === 'ent-5-website'),
  )

  const isMC20WebsiteEntitlementPlan = selectedCentersAndApps?.centers?.some(
    center =>
      MC_2_0_SIGPREMIERPLANS.includes(center.code) &&
      center.entitlements?.some(ent => ent.code === 'ent-5-website'),
  )

  const retainWebsiteAnsweredYes =
    retainWebsiteModalState === RETAIN_WEBSITE_MODAL_STATES.ANSWERED_YES

  const isWebsiteFeeNotApplicable =
    (isWebsiteAppAlreadyIncluded && isWebsiteAddedAsEntitlement) ||
    retainWebsiteAnsweredYes

  const isWebsiteDesignFeeWaived =
    isWebsiteFeeNotApplicable ||
    isWebsiteValid ||
    hasPaidDesignFee ||
    isMC20WebsiteEntitlementPlan

  const handlePrevStep = () => {
    if (setOrder && order) {
      setOrder({...order, selectedCentersAndApps})
    }
    prevStep()
  }

  return (
    <CommandCenterLayout
      prevStep={handlePrevStep}
      columnAmount={3}
      title="Catalog"
      centeredHeader={true}
      isLoading={isLoading}
      showBackButton={!isUpgrade}
      setShowUpdateAddressModal={setShowUpdateAddressModal}
      accountInfo={
        isUpgrade
          ? accountInfo
          : accountInfo?.company || accountInfo?.firstName
          ? {
              recurlyCompany: accountInfo.company,
              recurlyFirst: accountInfo.firstName,
              recurlyLast: accountInfo.lastName,
              thryvID:
                accountInfo?.thryvId || CCAccountInfo?.thryvId || undefined,
              billTo: accountInfo?.billTo || order?.accountInformation?.billTo,
            }
          : undefined
      }
    >
      {showUpdateAddressModal && (
        <UpdateAddressModal
          is5Order={true}
          nextStep={nextStep}
          selectedCountry={selectedCountry}
          accountInfo={accountInfo}
          openOnRender={true}
          updateAddressInfo={updateAddressInfo}
          setShowUpdateAddressModal={setShowUpdateAddressModal}
          // accountSubscriptionData={accountSubscriptionData}
          order={order}
          hasKeapAccountAbility={hasKeapAccountAbility}
          setHasKeapAccountAbility={setHasKeapAccountAbility}
          keapAccount={keapAccount}
          setKeapAccount={setKeapAccount}
          keapID={keapID}
          setKeapID={setKeapID}
          handleStartOver={handleStartOver}
        />
      )}
      <CentersAndApps
        data={
          plans
            ? {
                ...plans,
                centers: plans.centers.filter(
                  c => !alternatePlanCodesToHide.has(c.code),
                ),
              }
            : plans
        }
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        selectedCentersAndApps={selectedCentersAndApps}
        defaultCentersAndApps={defaultCentersAndApps.current}
        expiredCentersAndApps={expiredCentersAndApps.current}
        setSelectedOptions={setSelectedOptions}
        setQuantity={setQuantity}
        isUpgrade={isUpgrade}
        isSEOMBGDisabled={isSEOMBGDisabled}
        setIsWebsiteSettingsModalVisible={setIsWebsiteSettingsModalVisible}
        selectedCountry={selectedCountry}
        checkLegacySEO={accountSubscriptions?.accountInfo?.legacySEO}
        accountSubscriptionData={accountSubscriptionData}
        hasBusinessCenterAccess={hasBusinessCenterAccess}
        hasMarketingCenterAccess={hasMarketingCenterAccess}
        hasKeapMaxAccess={hasKeapMaxAccess}
        hasKeapHVACAccess={hasKeapHVACAccess}
        hasCustomDataFYAccess={hasCustomDataFYAccess}
        // hasCustomDataServiceAccess={hasCustomDataServiceAccess}
        isNewTLMCAccessible={isNewTLMCAccessible}
        keapAccount={keapAccount}
        keapAddUsersRecurlyQty={keapAddUsersRecurlyQty}
        keapAddUsersRecurlyQtyAnnual={keapAddUsersRecurlyQtyAnnual}
        kccRecurlyOption={kccRecurlyOption}
        upgradePaymentPreview={upgradePaymentPreview}
      />
      <Options
        apps={plans?.apps}
        selectedCountry={selectedCountry}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        selectedCentersAndApps={selectedCentersAndApps}
        setSelectedCentersAndApps={setSelectedCentersAndApps}
        proposalId={proposalId}
        setProposalId={setProposalId}
        proposalValidation={proposalValidation}
        setProposalValidation={setProposalValidation}
        alphaSEOFromRecurly={alphaSEOFromRecurly}
        alphaProposalId={alphaProposalId}
        setAlphaProposalId={setAlphaProposalId}
        alphaDomain={alphaDomain}
        setAlphaDomain={setAlphaDomain}
        alphaAmount={alphaAmount}
        setAlphaAmount={setAlphaAmount}
        // categories={categories}
        // setCategories={setCategories}
        // geos={geos}
        // setGeos={setGeos}
        isExternalDomain={isExternalDomain}
        setIsExternalDomain={setIsExternalDomain}
        alphaKeyCount={alphaKeyCount}
        setAlphaKeyCount={setAlphaKeyCount}
        alphaPowerboostCount={alphaPowerboostCount}
        setAlphaPowerboostCount={setAlphaPowerboostCount}
        diableValidateAndProposalInput={diableValidateAndProposalInput}
        selectedOptions={selectedOptions}
        setSelectedOptions={setSelectedOptions}
        paymentType={paymentType}
        setPaymentType={setPaymentType}
        onboardingRequired={onboardingRequired}
        setOnboardingRequired={setOnboardingRequired}
        onboardingFeeRequired={onboardingFeeRequired}
        setOnboardingFeeRequired={setOnboardingFeeRequired}
        includeOnboarding={includeOnboarding}
        setIncludeOnboarding={setIncludeOnboarding}
        mcDomainConfig={mcDomainConfig}
        setMcDomainConfig={setMcDomainConfig}
        tlDomainConfig={tlDomainConfig}
        setTlDomainConfig={setTlDomainConfig}
        llDomainConfig={llDomainConfig}
        setLlDomainConfig={setLlDomainConfig}
        existingMc2DomainValue={existingMc2DomainValue}
        seoDomainConfig={seoDomainConfig}
        setSeoDomainConfig={setSeoDomainConfig}
        hvacDomainConfig={hvacDomainConfig}
        setHvacDomainConfig={setHvacDomainConfig}
        thryvLeadsId={thryvLeadsId}
        setThryvLeadsId={setThryvLeadsId}
        thryvLeadsValidation={thryvLeadsValidation}
        setThryvLeadsValidation={setThryvLeadsValidation}
        futureBillingDate={futureBillingDate}
        setFutureBillingDate={setFutureBillingDate}
        sensisSiteDomain={sensisSiteDomain}
        setSensisSiteDomain={setSensisSiteDomain}
        quantity={quantity}
        setQuantity={setQuantity}
        seatQty={seatQty}
        isUpgrade={isUpgrade}
        oneTimeFees={plans?.oneTimes.filter(
          oneTime => oneTime.appliesTo.length > 0,
        )}
        centers={plans?.centers}
        isSEOMBGDisabled={isSEOMBGDisabled}
        setIsSEOMBGDisabled={setIsSEOMBGDisabled}
        defaultCentersAndApps={defaultCentersAndApps.current}
        expiredCentersAndApps={expiredCentersAndApps.current}
        isWebsiteSettingsModalVisible={isWebsiteSettingsModalVisible}
        setIsWebsiteSettingsModalVisible={setIsWebsiteSettingsModalVisible}
        isWebsiteValid={isWebsiteValid}
        setIsWebsiteValid={setIsWebsiteValid}
        setIsEaidFound={setIsEaidFound}
        isEaidFound={isEaidFound}
        setCohortEaid={setCohortEaid}
        cohortEaid={cohortEaid}
        upgradePaymentPreview={upgradePaymentPreview}
        setUpgradePaymentPreview={value =>
          couponDispatch(setUpgradePaymentPreviewAction(value))
        }
        setIsInvoiceBilled={setIsInvoiceBilled}
        setAllowInvoice={setAllowInvoice}
        setIsCohort={setIsCohort}
        isLegacyUpgradeAllowed={isLegacyUpgradeAllowed}
        setIsLegacyUpgradeAllowed={setIsLegacyUpgradeAllowed}
        thryvId={CCAccountInfo.thryvId}
        hasPaidDesignFee={
          accountInfo.hasPaidDesignFee ||
          accountSubscriptionData?.accountInfo?.hasPaidDesignFee
        }
        hasPaidOnboardingFee={
          accountInfo.hasPaidOnboardingFee ||
          accountSubscriptionData?.accountInfo?.hasPaidOnboardingFee
        }
        isNewTLMCAccessible={isNewTLMCAccessible}
        keapAccount={keapAccount}
        keapDomainConfig={keapDomainConfig}
        setKeapDomainConfig={setKeapDomainConfig}
        isWebsiteDesignFeeWaived={isWebsiteDesignFeeWaived}
        retainWebsiteModalState={retainWebsiteModalState}
        setRetainWebsiteModalState={setRetainWebsiteModalState}
        mc20WebsiteDowngradeModalState={mc20WebsiteDowngradeModalState}
        setMc20WebsiteDowngradeModalState={setMc20WebsiteDowngradeModalState}
        mc20WebsiteDowngradePlanName={mc20WebsiteDowngradePlanName}
        setMc20WebsiteDowngradePlanName={setMc20WebsiteDowngradePlanName}
        keapTrailAppId={keapTrailAppId}
        setKeapTrailAppId={setKeapTrailAppId}
        isFreeTrailCustomer={isFreeTrailCustomer}
        setIsFreeTrailCustomer={setIsFreeTrailCustomer}
        setKeapTextVoiceQty={setKeapTextVoiceQty}
        setKeapTextVoiceQtyAnnual={setKeapTextVoiceQtyAnnual}
        setKeapAddContactsQty={setKeapAddContactsQty}
        setKeapAddContactsQtyAnnual={setKeapAddContactsQtyAnnual}
        setKeapAddContactsQtyGraphlyAnnual={setKeapAddContactsQtyGraphlyAnnual}
        setPlusThisPremiumQtyGraphlyAnnual={setPlusThisPremiumQtyGraphlyAnnual}
        setKeapAddContactsQtyGraphlyM2m={setKeapAddContactsQtyGraphlyM2m}
        setPlusThisPremiumQtyGraphlyM2m={setPlusThisPremiumQtyGraphlyM2m}
        selectedAdditionalContactOption={selectedAdditionalContactOption}
        setSelectedAdditionalContactOption={setSelectedAdditionalContactOption}
        selectedAdditionalContactOptionAnnual={
          selectedAdditionalContactOptionAnnual
        }
        setSelectedAdditionalContactOptionAnnual={
          setSelectedAdditionalContactOptionAnnual
        }
        selectedAdditionalContactOptionGraphlyAnnual={
          selectedAdditionalContactOptionGraphlyAnnual
        }
        setSelectedAdditionalContactOptionGraphlyAnnual={
          setSelectedAdditionalContactOptionGraphlyAnnual
        }
        selectedAdditionalContactOptionGraphlyM2m={
          selectedAdditionalContactOptionGraphlyM2m
        }
        setSelectedAdditionalContactOptionGraphlyM2m={
          setSelectedAdditionalContactOptionGraphlyM2m
        }
        selectedPlusThisPremiumAnnual={selectedPlusThisPremiumAnnual}
        setSelectedPlusThisPremiumAnnual={setSelectedPlusThisPremiumAnnual}
        selectedPlusThisPremiumM2m={selectedPlusThisPremiumM2m}
        setSelectedPlusThisPremiumM2m={setSelectedPlusThisPremiumM2m}
        selectedKeapTextVoice={selectedKeapTextVoice}
        setSelectedKeapTextVoice={setSelectedKeapTextVoice}
        selectedKeapTextVoiceAnnual={selectedKeapTextVoiceAnnual}
        setSelectedKeapTextVoiceAnnual={setSelectedKeapTextVoiceAnnual}
        keapAddContactsRecurlyQty={keapAddContactsRecurlyQty}
        keapTextVoiceRecurlyQty={keapTextVoiceRecurlyQty}
        keapAddUsersRecurlyQty={keapAddUsersRecurlyQty}
        keapAddUsersRecurlyQtyAnnual={keapAddUsersRecurlyQtyAnnual}
        keapAddContactsRecurlyQtyAnnual={keapAddContactsRecurlyQtyAnnual}
        plusThisPremiumRecurlyQtyAnnual={plusThisPremiumRecurlyQtyAnnual}
        keapAddContactsRecurlyQtyGraphlyAnnual={
          keapAddContactsRecurlyQtyGraphlyAnnual
        }
        keapAddContactsRecurlyQtyGraphlyM2m={
          keapAddContactsRecurlyQtyGraphlyM2m
        }
        plusThisPremiumRecurlyQtyM2m={plusThisPremiumRecurlyQtyM2m}
        selectedKcpOption={selectedKcpOption}
        setSelectedKcpOption={setSelectedKcpOption}
        kcpRecurlyOption={kcpRecurlyOption}
        kccRecurlyOption={kccRecurlyOption}
        all5PlanPrices={all5PlanPrices}
      />
      <OrderSummary
        nextStep={nextStep}
        selectedCentersAndApps={selectedCentersAndApps}
        isWebsiteValid={isWebsiteValid}
        isWebsiteDesignFeeWaived={
          isWebsiteDesignFeeWaived ||
          mc20WebsiteDowngradeModalState ===
            RETAIN_WEBSITE_MODAL_STATES.ANSWERED_YES
        }
        hasPaidDesignFee={
          accountInfo.hasPaidDesignFee ||
          accountSubscriptionData?.accountInfo?.hasPaidDesignFee
        }
        code={code}
        setCode={setCode}
        handleStartOver={handleStartOver}
        oneTimeFees={plans?.oneTimes.filter(
          oneTime => oneTime.appliesTo.length > 0,
        )}
        selectedCountry={selectedCountry}
        isUpgrade={isUpgrade}
        setAccountInfo={setAccountInfo}
        accountInfo={accountInfo}
        order={order}
        totalPrice={totalPrice}
        defaultCentersAndApps={defaultCentersAndApps.current}
        expiredCentersAndApps={expiredCentersAndApps.current}
        all5PlanPrices={all5PlanPrices}
        monthlyTotal={monthlyTotal}
        allowInvoice={allowInvoice}
        plans={plans}
        orderData5CU={orderData5CU}
        setOrderData5CU={setOrderData5CU}
        captureOrder5NewUpgrade={captureOrder5NewUpgrade}
        isNewTLMCAccessible={isNewTLMCAccessible}
        hasKeapAccountAbility={hasKeapAccountAbility}
        setHasKeapAccountAbility={setHasKeapAccountAbility}
        keapAccount={keapAccount}
        setKeapAccount={setKeapAccount}
        keapID={keapID}
        setKeapID={setKeapID}
        isBillingNoticeDisplayed={isBillingNoticeDisplayed}
        setIsBillingNoticeDisplayed={setIsBillingNoticeDisplayed}
        handleRemoveItem={handleRemoveItem}
        thryvId={CCAccountInfo?.thryvId}
        ccId={CCAccountInfo?.ccId}
        setCCAccountInfo={updates =>
          setOrder(prev => ({
            ...prev,
            CCAccountInfo: {
              ...(prev?.CCAccountInfo || CCAccountInfo),
              ...updates,
            },
          }))
        }
        onboardingFeeRequired={onboardingFeeRequired}
        includeOnboarding={includeOnboarding}
        upgradePaymentPreview={upgradePaymentPreview}
      />
    </CommandCenterLayout>
  )
}

export {Catalog}
